import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { Link } from 'react-router-dom'
import {
  INVENTORY,
  CATEGORIES,
  EVENT_TYPES,
  SERVICE_TYPES,
  PACKAGE_PRESETS,
  calcLine,
} from '../data/inventory'

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmtINR = (n) =>
  '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })

const todayISO = () => new Date().toISOString().slice(0, 10)

// Numeric-input helpers — allow truly empty fields. We display 0 as ""
// so the user can delete the value freely, and parse "" back to 0 internally.
const showNum = (v) => (v === 0 || v === '0' || v == null || v === '' ? '' : v)
const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const fmtTime12 = (t) => {
  if (!t) return ''
  const [h, m] = String(t).split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return String(t)
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`
}

const fmtTimeRange = (start, end) => {
  const s = fmtTime12(start)
  const e = fmtTime12(end)
  if (s && e) return `${s} – ${e}`
  return s || e || '—'
}

// URL-safe base64 of a JSON state. Browsers cap URLs around 8–32KB; small/medium
// quotes encode to a few KB which is well within limits.
const encodeShare = (state) => {
  const json = JSON.stringify(state)
  // btoa needs latin1; this round-trip handles unicode correctly
  const b64 = btoa(unescape(encodeURIComponent(json)))
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}
const decodeShare = (encoded) => {
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const json = decodeURIComponent(escape(atob(padded)))
    return JSON.parse(json)
  } catch {
    return null
  }
}

const buildSummary = (state) => {
  const ev = state.event || {}
  // Best-effort total recompute (we don't depend on calcLine here; we reuse the
  // saved grandTotal if present, else 0). The drawer card is purely informational.
  return {
    quoteNo: state.quoteNo,
    savedAt: new Date().toISOString(),
    customerName:
      [ev.salutation, ev.guestName].filter(Boolean).join(' ') ||
      ev.guestName ||
      '—',
    contact: ev.contact,
    eventType: ev.eventType,
    eventDate: ev.date,
    startTime: ev.startTime || ev.time,
    endTime: ev.endTime,
    location: ev.location,
    serviceType: ev.serviceType,
    packageName: ev.packageName,
    guests: ev.guestCount,
    grandTotal: state.grandTotal || 0,
  }
}

// Compute the four package grand totals for a stored quote — used in the
// Saved Quotes drawer so each card shows Starter / Basic / Premium / Custom
// pricing at a glance.
const computeAllPackageTotals = (state) => {
  if (!state || !state.event) return null
  const event = state.event
  const savedPackageSelections =
    state.packageSelections && typeof state.packageSelections === 'object'
      ? state.packageSelections
      : null
  // Legacy fallback: single flat `selected` blob (pre per-package refactor)
  const legacySelected = state.selected || {}
  const customItems = state.customItems || []
  // Per-package charges (new schema) with fallback to flat (legacy schema).
  const flatTravel = Number(state.travelCharge || 0)
  const flatProfitPct = Number(state.overallProfitPct || 0)
  const flatRoundOff = Number(state.roundOff || 0)
  const flatRoundMode = state.roundMode || '5000'
  const pkgChargesMap = state.packageCharges || {}
  const chargesFor = (pkg) =>
    pkgChargesMap[pkg] || {
      travelCharge: flatTravel,
      overallProfitPct: flatProfitPct,
      roundOff: flatRoundOff,
      roundMode: flatRoundMode,
    }
  const guests = Number(event.guestCount || 0)
  const serviceType = event.serviceType || 'Catering'
  const allItems = [...INVENTORY, ...customItems]

  const totalFor = (pkg) => {
    const fromUser = savedPackageSelections?.[pkg]
    let pkgSelected
    if (fromUser && Object.keys(fromUser).length > 0) {
      pkgSelected = fromUser
    } else if (Object.keys(legacySelected).length > 0) {
      // legacy quote: apply same flat selection to all packages
      pkgSelected = legacySelected
    } else {
      const presetIds = PACKAGE_PRESETS?.[serviceType]?.[pkg]
      pkgSelected = Array.isArray(presetIds)
        ? Object.fromEntries(presetIds.map((id) => [id, {}]))
        : {}
    }
    const lines = allItems
      .filter(
        (i) => pkgSelected[i.id] !== undefined && i.serviceType === serviceType,
      )
      .map((item) => {
        const ov = pkgSelected[item.id] || {}
        return calcLine(item, guests, ov, { packageName: pkg })
      })
    const itemsSubtotal = lines.reduce((s, l) => s + l.total, 0)
    const ch = chargesFor(pkg)
    const preProfit = itemsSubtotal + Number(ch.travelCharge || 0)
    const profit = Math.round(
      (preProfit * Number(ch.overallProfitPct || 0)) / 100,
    )
    const beforeRound = preProfit + profit
    let roundOff = 0
    if (ch.roundMode === 'manual') {
      roundOff = Number(ch.roundOff || 0)
    } else {
      const step = Number(ch.roundMode || '5000')
      if (step && beforeRound > 0) {
        const target = Math.max(step, Math.round(beforeRound / step) * step)
        roundOff = target - beforeRound
      }
    }
    return beforeRound + roundOff
  }

  return {
    Starter: totalFor('Starter'),
    Basic: totalFor('Basic'),
    Premium: totalFor('Premium'),
    Custom: totalFor('Custom'),
  }
}

const generateQuoteNo = () => {
  // Q-YYYYMMDDHHMMSS-XXX (seconds-precision + 3-char random suffix → always unique)
  const ts = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14)
  const suffix = Math.random().toString(36).slice(2, 5).toUpperCase()
  return `Q-${ts}-${suffix}`
}

const fullName = (e) =>
  [e.salutation, e.guestName].filter((x) => x && String(x).trim()).join(' ')

const fullContact = (e) =>
  [e.contactCode, e.contact].filter((x) => x && String(x).trim()).join(' ')

const eventSummary = (e) => {
  const parts = []
  if (e.serviceType) parts.push(e.serviceType)
  const name = fullName(e)
  if (name) parts.push(name)
  parts.push(`${e.guestCount} guests`)
  if (e.eventType) parts.push(e.eventType)
  return parts.join(' · ')
}

const eventLineTwo = (e) => {
  const parts = []
  if (e.date)
    parts.push(
      new Date(e.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    )
  const startStr = fmtTime12(e.startTime || e.time)
  const endStr = fmtTime12(e.endTime)
  if (startStr && endStr) parts.push(`${startStr} – ${endStr}`)
  else if (startStr) parts.push(startStr)
  if (e.location) parts.push(e.location)
  return parts.join(' · ')
}

const blankCustomItem = (serviceType = 'Catering') => ({
  id: 'custom-' + Math.random().toString(36).slice(2, 9),
  name: '',
  category: 'Custom',
  unitLabel: 'Nos',
  rule: 'flat',
  flatPrice: 0,
  custom: true,
  serviceType,
})

// ─── main ────────────────────────────────────────────────────────────────────

const Quote = () => {
  const [event, setEvent] = useState({
    serviceType: 'Catering',
    salutation: 'Mr',
    guestName: '',
    contactCode: '+91',
    contact: '',
    guestCount: 100,
    location: '',
    eventType: 'Birthday',
    date: todayISO(),
    startTime: '18:00',
    endTime: '22:00',
    packageName: 'Starter',
  })
  const [eventOpen, setEventOpen] = useState(true)
  const PACKAGES = ['Starter', 'Basic', 'Premium', 'Custom']
  const SALUTATIONS = ['Mr', 'Mrs', 'Ms', 'Mx', 'Dr']
  const COUNTRY_CODES = ['+91', '+1', '+44', '+971', '+65', '+61', '+966']

  // ── Per-package financial settings ─────────────────────────
  // Each package keeps its own travel charge / profit % / round-off / round mode.
  const PKG_NAMES = ['Starter', 'Basic', 'Premium', 'Custom']
  const defaultPkgCharges = () => ({
    travelCharge: 0,
    overallProfitPct: 20,
    roundOff: 0,
    roundMode: '5000',
  })
  const [packageCharges, setPackageCharges] = useState(() =>
    Object.fromEntries(PKG_NAMES.map((p) => [p, defaultPkgCharges()])),
  )

  // Current-package shortcuts (mirror the old API so the rest of the file keeps working).
  const currentPkgName = event.packageName || 'Custom'
  const currentCharges =
    packageCharges[currentPkgName] || defaultPkgCharges()
  const travelCharge = currentCharges.travelCharge
  const overallProfitPct = currentCharges.overallProfitPct

  const updateCurrentCharges = (patch) => {
    setPackageCharges((pc) => ({
      ...pc,
      [currentPkgName]: {
        ...(pc[currentPkgName] || defaultPkgCharges()),
        ...patch,
      },
    }))
  }
  const setTravelCharge = (v) => updateCurrentCharges({ travelCharge: v })
  const setOverallProfitPct = (v) =>
    updateCurrentCharges({ overallProfitPct: v })

  // Per-package item selections. Each package keeps its own customised
  // selection so switching between packages preserves what the user changed.
  const initPackageSelections = (serviceType = 'Catering') => {
    const init = {}
    for (const pkg of ['Starter', 'Basic', 'Premium', 'Custom']) {
      const ids = PACKAGE_PRESETS?.[serviceType]?.[pkg]
      init[pkg] = Array.isArray(ids)
        ? Object.fromEntries(ids.map((id) => [id, {}]))
        : {}
    }
    return init
  }
  const [packageSelections, setPackageSelections] = useState(() =>
    initPackageSelections('Catering'),
  )

  // The "selected" view exposed to the rest of the file is the CURRENT package's
  // selection. Edits go to that package only.
  const selected =
    packageSelections[event.packageName || 'Custom'] || {}
  const setSelected = (updaterOrValue) => {
    setPackageSelections((ps) => {
      const pkgKey = event.packageName || 'Custom'
      const cur = ps[pkgKey] || {}
      const next =
        typeof updaterOrValue === 'function' ? updaterOrValue(cur) : updaterOrValue
      return { ...ps, [pkgKey]: next }
    })
  }
  const [editing, setEditing] = useState({}) // { [id]: bool }
  const [customItems, setCustomItems] = useState([])

  const [filter, setFilter] = useState('All')
  const [sheetOpen, setSheetOpen] = useState(false)

  // PDF mode + validation
  const [pdfMode, setPdfMode] = useState('client')
  const [printToken, setPrintToken] = useState(0) // bumped to trigger window.print()
  const [showErrors, setShowErrors] = useState(false)

  const contactDigits = (event.contact || '').replace(/\D/g, '')
  const isIndiaCode = (event.contactCode || '+91') === '+91'
  const phoneMin = isIndiaCode ? 10 : 7
  const phoneMax = isIndiaCode ? 10 : 15
  const errors = {
    guestName: !event.guestName?.trim(),
    contact:
      !event.contact?.trim() ||
      contactDigits.length < phoneMin ||
      contactDigits.length > phoneMax,
    eventType: !event.eventType?.trim(),
    location: !event.location?.trim(),
  }
  const hasErrors = Object.values(errors).some(Boolean)

  // Quote number — generated on first load, replaced when loading a saved quote
  const [quoteNo, setQuoteNo] = useState(generateQuoteNo)

  // Saved quotes (localStorage)
  const INDEX_KEY = 'aura_quotes_index'
  const itemKey = (qn) => `aura_quote_${qn}`
  const [savedQuotes, setSavedQuotes] = useState([])
  const [savedListOpen, setSavedListOpen] = useState(false)
  const [shareTarget, setShareTarget] = useState(null) // { state, quoteNo }
  const [shareToast, setShareToast] = useState('')
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewMode, setPreviewMode] = useState('client') // 'client' | 'internal'
  const [recommendedPackage, setRecommendedPackage] = useState(null) // 'Starter' | 'Basic' | 'Premium' | null
  const importInputRef = useRef(null)
  const [justSaved, setJustSaved] = useState(false)

  // Load index on first mount
  useEffect(() => {
    try {
      const idx = JSON.parse(localStorage.getItem(INDEX_KEY) || '[]')
      if (Array.isArray(idx)) setSavedQuotes(idx)
    } catch {}
  }, [])

  // index.css applies transform + perspective to <body> as a "performance"
  // optimization, which makes <body> the containing block for any
  // position:fixed descendants (drawer + bottom sheet). Neutralise them on
  // this page so fixed positioning resolves to the viewport, then restore on
  // unmount.
  useEffect(() => {
    const body = document.body
    const prev = {
      transform: body.style.transform,
      webkitTransform: body.style.webkitTransform,
      perspective: body.style.perspective,
      webkitPerspective: body.style.webkitPerspective,
      backfaceVisibility: body.style.backfaceVisibility,
      webkitBackfaceVisibility: body.style.webkitBackfaceVisibility,
    }
    body.style.transform = 'none'
    body.style.webkitTransform = 'none'
    body.style.perspective = 'none'
    body.style.webkitPerspective = 'none'
    body.style.backfaceVisibility = 'visible'
    body.style.webkitBackfaceVisibility = 'visible'
    return () => {
      body.style.transform = prev.transform
      body.style.webkitTransform = prev.webkitTransform
      body.style.perspective = prev.perspective
      body.style.webkitPerspective = prev.webkitPerspective
      body.style.backfaceVisibility = prev.backfaceVisibility
      body.style.webkitBackfaceVisibility = prev.webkitBackfaceVisibility
    }
  }, [])

  // Enrich each saved-quote summary with the 4 package totals (only when
  // the drawer is open — avoids any work otherwise).
  const enrichedSavedQuotes = useMemo(() => {
    if (!savedListOpen) return savedQuotes
    return savedQuotes.map((q) => {
      try {
        const raw = localStorage.getItem(itemKey(q.quoteNo))
        if (!raw) return q
        const state = JSON.parse(raw)
        return { ...q, packageTotals: computeAllPackageTotals(state) }
      } catch {
        return q
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedListOpen, savedQuotes])

  // Lock body scroll while drawer open
  useEffect(() => {
    if (!savedListOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [savedListOpen])

  useEffect(() => {
    if (printToken === 0) return
    // Browsers use document.title as the default "Save as PDF" filename.
    // Switch it to a quote-specific name for the duration of the print dialog,
    // then restore it.
    const originalTitle = document.title
    const safeName = (event.guestName || 'Customer')
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32)
    const modeLabel = pdfMode === 'client' ? 'Client' : 'Internal'
    const parts = [modeLabel]
    if (event.date) parts.push(event.date) // YYYY-MM-DD
    if (event.serviceType) parts.push(event.serviceType)
    if (event.packageName) parts.push(event.packageName)
    parts.push(safeName || 'Quote')
    parts.push(quoteNo)
    document.title = parts.join('_')

    const restore = () => {
      document.title = originalTitle
      window.removeEventListener('afterprint', restore)
    }
    window.addEventListener('afterprint', restore)
    // Fallback restore after a few seconds in case afterprint doesn't fire
    const t = setTimeout(restore, 6000)

    window.print()
    return () => clearTimeout(t)
  }, [printToken])

  const allItems = useMemo(() => [...INVENTORY, ...customItems], [customItems])
  const idsBySection = filter
  // We always render all items, ordered by category then by source.

  const lines = useMemo(
    () =>
      allItems
        .filter(
          (i) =>
            selected[i.id] !== undefined && i.serviceType === event.serviceType,
        )
        .map((item) => {
          const ov = selected[item.id] || {}
          return {
            item,
            override: ov,
            ...calcLine(item, event.guestCount, ov, { packageName: event.packageName }),
          }
        }),
    [allItems, selected, event.guestCount, event.packageName, event.serviceType],
  )

  // Per-package round-off — derived from packageCharges[currentPkg]
  const roundOff = currentCharges.roundOff || 0
  const roundMode = currentCharges.roundMode || '5000'
  const setRoundOff = (v) => updateCurrentCharges({ roundOff: v })
  const setRoundMode = (v) => updateCurrentCharges({ roundMode: v })

  const itemsSubtotal = lines.reduce((s, l) => s + l.total, 0)
  const preProfitTotal = itemsSubtotal + Number(travelCharge || 0)
  const overallProfit = Math.round((preProfitTotal * Number(overallProfitPct || 0)) / 100)
  const beforeRound = preProfitTotal + overallProfit
  const grandTotal = beforeRound + Number(roundOff || 0)
  // Round-off is conceptually additional profit (positive rounds up = extra; negative = give-away)
  const totalProfit = overallProfit + Number(roundOff || 0)
  // Effective profit % on items+travel after factoring in the round-off.
  const totalProfitPct =
    preProfitTotal > 0
      ? Math.round((totalProfit / preProfitTotal) * 1000) / 10 // 1 decimal
      : 0

  // Auto round-off whenever totals change (or step changes).
  // e.g. 4800 → 5000, 21000 → 20000, 24000 → 25000 (default step ₹5K).
  // Floor target to one step so small quotes don't collapse to ₹0.
  useEffect(() => {
    if (roundMode === 'manual') return
    const step = Number(roundMode)
    if (!step) return
    const target =
      beforeRound <= 0
        ? 0
        : Math.max(step, Math.round(beforeRound / step) * step)
    const delta = target - beforeRound
    if (delta !== Number(roundOff)) setRoundOff(delta)
  }, [beforeRound, roundMode]) // intentionally not depending on roundOff

  // ── Auto-save (debounced) ───────────────────────────────────────
  useEffect(() => {
    const hasContent =
      event.guestName.trim().length > 0 &&
      (Object.keys(selected).length > 0 || customItems.length > 0)
    if (!hasContent) return

    const t = setTimeout(() => {
      try {
        const state = {
          event,
          packageSelections,
          customItems,
          packageCharges,
          quoteNo,
        }
        localStorage.setItem(itemKey(quoteNo), JSON.stringify(state))

        const summary = {
          quoteNo,
          savedAt: new Date().toISOString(),
          customerName: fullName(event) || event.guestName,
          contact: event.contact,
          eventType: event.eventType,
          eventDate: event.date,
          startTime: event.startTime,
          endTime: event.endTime,
          location: event.location,
          serviceType: event.serviceType,
          packageName: event.packageName,
          guests: event.guestCount,
          grandTotal,
        }
        const raw = localStorage.getItem(INDEX_KEY)
        const idx = raw ? JSON.parse(raw) : []
        const filtered = (Array.isArray(idx) ? idx : []).filter(
          (q) => q.quoteNo !== quoteNo,
        )
        filtered.unshift(summary)
        localStorage.setItem(INDEX_KEY, JSON.stringify(filtered))
        setSavedQuotes(filtered)
        setJustSaved(true)
        const reset = setTimeout(() => setJustSaved(false), 1200)
        return () => clearTimeout(reset)
      } catch {}
    }, 800)
    return () => clearTimeout(t)
  }, [
    event,
    packageSelections,
    customItems,
    packageCharges,
    grandTotal,
    quoteNo,
  ])

  const loadQuote = (qn) => {
    try {
      const raw = localStorage.getItem(itemKey(qn))
      if (!raw) return
      const s = JSON.parse(raw)
      // Backward-compat for older quotes
      const ev = s.event || {}
      if (ev.time && !ev.startTime) {
        ev.startTime = ev.time
        delete ev.time
      }
      if (!ev.salutation) ev.salutation = 'Mr'
      if (!ev.contactCode) ev.contactCode = '+91'
      setEvent(ev)

      // Restore per-package items. Migrate from old flat `selected` if needed:
      // assign the same flat selection to the currently-saved package and seed
      // the others with their presets.
      if (s.packageSelections && typeof s.packageSelections === 'object') {
        setPackageSelections({
          ...initPackageSelections(ev.serviceType || 'Catering'),
          ...s.packageSelections,
        })
      } else if (s.selected && typeof s.selected === 'object') {
        const seed = initPackageSelections(ev.serviceType || 'Catering')
        seed[ev.packageName || 'Custom'] = s.selected
        setPackageSelections(seed)
      } else {
        setPackageSelections(initPackageSelections(ev.serviceType || 'Catering'))
      }
      setCustomItems(s.customItems || [])

      // Migrate flat travel/profit/round → per-package, or load saved per-package map
      if (s.packageCharges && typeof s.packageCharges === 'object') {
        setPackageCharges({
          ...Object.fromEntries(PKG_NAMES.map((p) => [p, defaultPkgCharges()])),
          ...s.packageCharges,
        })
      } else {
        const flat = {
          travelCharge: Number(s.travelCharge || 0),
          overallProfitPct:
            s.overallProfitPct === undefined ? 20 : Number(s.overallProfitPct),
          roundOff: Number(s.roundOff || 0),
          roundMode: s.roundMode || '5000',
        }
        setPackageCharges(
          Object.fromEntries(PKG_NAMES.map((p) => [p, { ...flat }])),
        )
      }

      setQuoteNo(qn)
      setEditing({})
      setSavedListOpen(false)
      setEventOpen(false)
      setShowErrors(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {}
  }

  const cloneQuote = (qn) => {
    try {
      const raw = localStorage.getItem(itemKey(qn))
      if (!raw) return
      const s = JSON.parse(raw)
      const ev = s.event || {}
      // Backward-compat for older saves
      if (ev.time && !ev.startTime) {
        ev.startTime = ev.time
        delete ev.time
      }
      // Carry over everything EXCEPT customer-specific identity
      const clonedEvent = {
        ...ev,
        salutation: 'Mr',
        guestName: '',
        contactCode: ev.contactCode || '+91',
        contact: '',
      }
      setEvent(clonedEvent)
      if (s.packageSelections && typeof s.packageSelections === 'object') {
        setPackageSelections({
          ...initPackageSelections(clonedEvent.serviceType || 'Catering'),
          ...s.packageSelections,
        })
      } else if (s.selected && typeof s.selected === 'object') {
        const seed = initPackageSelections(clonedEvent.serviceType || 'Catering')
        seed[clonedEvent.packageName || 'Custom'] = s.selected
        setPackageSelections(seed)
      } else {
        setPackageSelections(
          initPackageSelections(clonedEvent.serviceType || 'Catering'),
        )
      }
      setCustomItems(s.customItems || [])
      // Carry over per-package charges from the source (or migrate flat → all 4)
      if (s.packageCharges && typeof s.packageCharges === 'object') {
        setPackageCharges({
          ...Object.fromEntries(PKG_NAMES.map((p) => [p, defaultPkgCharges()])),
          ...s.packageCharges,
        })
      } else {
        const flat = {
          travelCharge: Number(s.travelCharge || 0),
          overallProfitPct:
            s.overallProfitPct === undefined ? 20 : Number(s.overallProfitPct),
          roundOff: 0,
          roundMode: '5000',
        }
        setPackageCharges(
          Object.fromEntries(PKG_NAMES.map((p) => [p, { ...flat }])),
        )
      }
      setEditing({})
      setQuoteNo(generateQuoteNo()) // fresh quote number
      setSavedListOpen(false)
      setEventOpen(true) // open form so user can fill customer details
      setShowErrors(false)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {}
  }

  const deleteQuote = (qn) => {
    try {
      localStorage.removeItem(itemKey(qn))
      const raw = localStorage.getItem(INDEX_KEY)
      const idx = raw ? JSON.parse(raw) : []
      const filtered = (Array.isArray(idx) ? idx : []).filter(
        (q) => q.quoteNo !== qn,
      )
      localStorage.setItem(INDEX_KEY, JSON.stringify(filtered))
      setSavedQuotes(filtered)
    } catch {}
  }

  // ── Share / Import ────────────────────────────────────────────
  const flashToast = (msg) => {
    setShareToast(msg)
    setTimeout(() => setShareToast(''), 2400)
  }

  const openShare = (qn) => {
    try {
      const raw = localStorage.getItem(itemKey(qn))
      if (!raw) return
      const state = JSON.parse(raw)
      setShareTarget({ state, quoteNo: qn })
    } catch {}
  }

  const buildShareUrl = (state) =>
    `${window.location.origin}/quote#q=${encodeShare(state)}`

  const doNativeShare = async () => {
    if (!shareTarget) return
    const url = buildShareUrl(shareTarget.state)
    const ev = shareTarget.state.event || {}
    const title = `Quote · ${[ev.salutation, ev.guestName].filter(Boolean).join(' ') || shareTarget.quoteNo}`
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: `AuraSquare Events Quote — ${shareTarget.quoteNo}`,
          url,
        })
      } catch {}
    } else {
      doCopyLink()
    }
  }

  const doCopyLink = async () => {
    if (!shareTarget) return
    const url = buildShareUrl(shareTarget.state)
    try {
      await navigator.clipboard.writeText(url)
      flashToast('Share link copied')
    } catch {
      flashToast('Copy failed — long-press to copy manually')
    }
  }

  const doDownloadJson = () => {
    if (!shareTarget) return
    const json = JSON.stringify(shareTarget.state, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${shareTarget.quoteNo}.json`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 500)
    flashToast('Downloaded')
  }

  // Persist a state object into localStorage (used by import + hash auto-load)
  const importStateIntoStore = (state) => {
    if (!state || typeof state !== 'object' || !state.event) return null
    let qn = state.quoteNo || generateQuoteNo()
    // If a different quote with the same id already exists, mint a fresh id
    if (
      localStorage.getItem(itemKey(qn)) &&
      JSON.stringify(JSON.parse(localStorage.getItem(itemKey(qn)))) !==
        JSON.stringify(state)
    ) {
      qn = generateQuoteNo()
    }
    const stored = { ...state, quoteNo: qn }
    localStorage.setItem(itemKey(qn), JSON.stringify(stored))
    const summary = buildSummary(stored)
    const raw = localStorage.getItem(INDEX_KEY)
    const idx = raw ? JSON.parse(raw) : []
    const filtered = (Array.isArray(idx) ? idx : []).filter(
      (q) => q.quoteNo !== qn,
    )
    filtered.unshift(summary)
    localStorage.setItem(INDEX_KEY, JSON.stringify(filtered))
    setSavedQuotes(filtered)
    return qn
  }

  const importFromFile = (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const state = JSON.parse(e.target.result)
        const qn = importStateIntoStore(state)
        if (qn) {
          loadQuote(qn)
          flashToast('Quote imported')
        } else {
          flashToast('Invalid quote file')
        }
      } catch {
        flashToast('Could not read file')
      }
    }
    reader.readAsText(file)
  }

  // Auto-import on /quote#q=... — drop the hash once handled.
  useEffect(() => {
    const m = window.location.hash.match(/[#&]q=([^&]+)/)
    if (!m) return
    const state = decodeShare(m[1])
    if (!state) {
      flashToast('Share link is invalid')
      window.history.replaceState(null, '', '/quote')
      return
    }
    const qn = importStateIntoStore(state)
    if (qn) {
      loadQuote(qn)
      flashToast('Loaded shared quote')
    }
    window.history.replaceState(null, '', '/quote')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // (Per-package selections are seeded with their presets at state init,
  // so no separate "first-mount apply preset" effect is needed.)

  const newQuote = () => {
    if (
      Object.keys(selected).length > 0 &&
      !window.confirm(
        'Start a new quote? Current quote stays saved and accessible from "Saved Quotes".',
      )
    )
      return
    setEvent({
      serviceType: 'Catering',
      salutation: 'Mr',
      guestName: '',
      contactCode: '+91',
      contact: '',
      guestCount: 100,
      location: '',
      eventType: 'Birthday',
      date: todayISO(),
      startTime: '19:00',
      endTime: '23:00',
      packageName: 'Starter',
    })
    // Seed all 4 packages with their respective presets
    setPackageSelections(initPackageSelections('Catering'))
    setCustomItems([])
    setPackageCharges(
      Object.fromEntries(PKG_NAMES.map((p) => [p, defaultPkgCharges()])),
    )
    setEditing({})
    setQuoteNo(generateQuoteNo())
    setEventOpen(true)
    setShowErrors(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const visibleCatalog = useMemo(() => {
    const byService = INVENTORY.filter((i) => i.serviceType === event.serviceType)
    return filter === 'All' ? byService : byService.filter((i) => i.category === filter)
  }, [filter, event.serviceType])

  // Categories that actually exist in the current service type's inventory.
  const availableCategories = useMemo(() => {
    const seen = new Set()
    INVENTORY.forEach((i) => {
      if (i.serviceType === event.serviceType) seen.add(i.category)
    })
    return Array.from(seen)
  }, [event.serviceType])

  // Reset category filter to "All" when switching service type so a stale
  // category (e.g. "Main Course" while on Photography) doesn't hide items.
  useEffect(() => {
    if (filter !== 'All' && !availableCategories.includes(filter)) {
      setFilter('All')
    }
  }, [availableCategories, filter])

  const visibleCustomItems = useMemo(
    () => customItems.filter((i) => i.serviceType === event.serviceType),
    [customItems, event.serviceType],
  )

  const toggleItem = (item) => {
    setSelected((s) => {
      const next = { ...s }
      if (next[item.id] !== undefined) delete next[item.id]
      else next[item.id] = {}
      return next
    })
    setEditing((e) => ({ ...e, [item.id]: false }))
  }

  const updateOverride = (itemId, key, value) =>
    setSelected((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), [key]: value } }))

  const resetOverride = (itemId, key) =>
    setSelected((s) => {
      const cur = { ...(s[itemId] || {}) }
      delete cur[key]
      return { ...s, [itemId]: cur }
    })

  const addCustomItem = () => {
    const c = blankCustomItem(event.serviceType)
    setCustomItems((xs) => [...xs, c])
    setSelected((s) => ({ ...s, [c.id]: {} }))
    setEditing((e) => ({ ...e, [c.id]: true }))
  }

  // Switch the active package. Items per package are kept in
  // packageSelections, so this just changes which package the user sees.
  const selectPackage = (pkg) => {
    setEvent((ev) => ({ ...ev, packageName: pkg }))
    setEditing({})
  }

  // Reset the CURRENT package's items back to its preset (discards customisation).
  const resetCurrentPackageToPreset = () => {
    const pkg = event.packageName || 'Custom'
    const ids = PACKAGE_PRESETS?.[event.serviceType]?.[pkg]
    const next = Array.isArray(ids)
      ? Object.fromEntries(ids.map((id) => [id, {}]))
      : {}
    setPackageSelections((ps) => ({ ...ps, [pkg]: next }))
    setEditing({})
  }

  const updateCustomField = (id, key, value) =>
    setCustomItems((xs) =>
      xs.map((x) =>
        x.id === id
          ? { ...x, [key]: key === 'name' || key === 'unitLabel' ? value : Number(value) || 0 }
          : x,
      ),
    )

  const removeCustomItem = (id) => {
    setCustomItems((xs) => xs.filter((x) => x.id !== id))
    setSelected((s) => {
      const n = { ...s }
      delete n[id]
      return n
    })
  }

  const handlePrint = (mode) => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setShowErrors(false)
    setPdfMode(mode)
    setPrintToken((t) => t + 1)
  }

  const handleDownloadImage = async (mode) => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setShowErrors(false)
    setPdfMode(mode)
    flashToast('Preparing image…')

    // Wait for the printable view to re-render with the new mode
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)))

    const node = document.querySelector('.print-area')?.firstElementChild
    if (!node) {
      flashToast('Could not capture image')
      return
    }

    try {
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      })

      const safeName = (event.guestName || 'Customer')
        .toString()
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 32)
      const modeLabel = mode === 'client' ? 'Client' : 'Internal'
      const parts = [modeLabel]
      if (event.date) parts.push(event.date)
      if (event.serviceType) parts.push(event.serviceType)
      if (event.packageName) parts.push(event.packageName)
      parts.push(safeName || 'Quote')
      parts.push(quoteNo)
      const filename = parts.join('_') + '.png'

      // Convert to blob and trigger download
      canvas.toBlob((blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 500)
        flashToast('Image downloaded')
      }, 'image/png')
    } catch (e) {
      console.error(e)
      flashToast('Image capture failed')
    }
  }

  // ── Compute the totals + lines for an arbitrary package without disturbing
  // current state. Used by the "All packages" multi-export.
  const buildPackageView = (pkg) => {
    // Use this package's CURRENT customised selection. Falls back to the
    // preset if the package has never been visited (or was emptied with no
    // custom override).
    const fromUser = packageSelections[pkg]
    let pkgSelected
    if (fromUser && Object.keys(fromUser).length > 0) {
      pkgSelected = fromUser
    } else {
      const presetIds = PACKAGE_PRESETS?.[event.serviceType]?.[pkg]
      pkgSelected = Array.isArray(presetIds)
        ? Object.fromEntries(presetIds.map((id) => [id, {}]))
        : {}
    }

    const allItems = [...INVENTORY, ...customItems]
    const pkgLines = allItems
      .filter(
        (i) =>
          pkgSelected[i.id] !== undefined && i.serviceType === event.serviceType,
      )
      .map((item) => {
        const ov = pkgSelected[item.id] || {}
        return {
          item,
          override: ov,
          ...calcLine(item, event.guestCount, ov, { packageName: pkg }),
        }
      })

    // Use this package's own charges (travel/profit/round) — independent per package.
    const charges = packageCharges[pkg] || defaultPkgCharges()
    const pkgTravel = Number(charges.travelCharge || 0)
    const pkgProfitPct = Number(charges.overallProfitPct || 0)
    const pkgManualRound = Number(charges.roundOff || 0)
    const pkgRoundModeVal = charges.roundMode || '5000'

    const pkgItemsSubtotal = pkgLines.reduce((s, l) => s + l.total, 0)
    const pkgPreProfit = pkgItemsSubtotal + pkgTravel
    const pkgProfit = Math.round((pkgPreProfit * pkgProfitPct) / 100)
    const pkgBeforeRound = pkgPreProfit + pkgProfit

    let pkgRoundOff = 0
    if (pkgRoundModeVal === 'manual') {
      pkgRoundOff = pkgManualRound
    } else {
      const step = Number(pkgRoundModeVal)
      if (step && pkgBeforeRound > 0) {
        const target = Math.max(
          step,
          Math.round(pkgBeforeRound / step) * step,
        )
        pkgRoundOff = target - pkgBeforeRound
      }
    }
    const pkgGrandTotal = pkgBeforeRound + pkgRoundOff
    const pkgTotalProfit = pkgProfit + pkgRoundOff
    const pkgTotalProfitPct =
      pkgPreProfit > 0
        ? Math.round((pkgTotalProfit / pkgPreProfit) * 1000) / 10
        : 0

    return {
      pkgEvent: { ...event, packageName: pkg },
      pkgLines,
      pkgItemsSubtotal,
      pkgTravel,
      pkgProfitPct,
      pkgProfit,
      pkgRoundOff,
      pkgTotalProfit,
      pkgTotalProfitPct,
      pkgGrandTotal,
    }
  }

  // Off-screen render + capture for a given package, without touching the
  // user's working state.
  const captureOffscreen = async (pkg, mode) => {
    const v = buildPackageView(pkg)

    const container = document.createElement('div')
    container.style.cssText =
      'position:absolute;left:-10000px;top:0;width:800px;background:#fff;font-family:Inter, sans-serif;'
    document.body.appendChild(container)

    const root = createRoot(container)
    flushSync(() => {
      root.render(
        mode === 'client' ? (
          <ClientQuote
            event={v.pkgEvent}
            lines={v.pkgLines}
            grandTotal={v.pkgGrandTotal}
            quoteNo={quoteNo}
          />
        ) : (
          <InternalQuote
            event={v.pkgEvent}
            lines={v.pkgLines}
            itemsSubtotal={v.pkgItemsSubtotal}
            travelCharge={Number(travelCharge) || 0}
            overallProfitPct={Number(overallProfitPct) || 0}
            overallProfit={v.pkgProfit}
            roundOff={v.pkgRoundOff}
            totalProfit={v.pkgTotalProfit}
            totalProfitPct={v.pkgTotalProfitPct}
            grandTotal={v.pkgGrandTotal}
            quoteNo={quoteNo}
          />
        ),
      )
    })

    // Wait for layout + image loading
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r)),
    )
    await new Promise((r) => setTimeout(r, 120))

    const node = container.firstElementChild
    let canvas
    try {
      const html2canvas = (await import('html2canvas')).default
      canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      })
    } finally {
      root.unmount()
      container.remove()
    }
    return { canvas, view: v }
  }

  // Render any React node off-screen and capture it to a canvas.
  const captureCustomOffscreen = async (element, width = 900) => {
    const container = document.createElement('div')
    container.style.cssText = `position:absolute;left:-10000px;top:0;width:${width}px;background:#fff;font-family:Inter, sans-serif;`
    document.body.appendChild(container)
    const root = createRoot(container)
    flushSync(() => root.render(element))
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r)),
    )
    await new Promise((r) => setTimeout(r, 120))
    const node = container.firstElementChild
    let canvas
    try {
      const html2canvas = (await import('html2canvas')).default
      canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      })
    } finally {
      root.unmount()
      container.remove()
    }
    return canvas
  }

  // Render the captured content onto a true A4-portrait canvas (white bg).
  // Content is anchored to the TOP so the header + customer details stay at
  // the top of the page no matter how short the package columns end up.
  const fitToA4 = (rawCanvas, orientation = 'portrait') => {
    // A4 at ~150 dpi for a crisp shareable image.
    const A4 = orientation === 'landscape'
      ? { w: 1754, h: 1240 }
      : { w: 1240, h: 1754 }
    const margin = 60
    const out = document.createElement('canvas')
    out.width = A4.w
    out.height = A4.h
    const ctx = out.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, A4.w, A4.h)
    const availW = A4.w - margin * 2
    const availH = A4.h - margin * 2
    const ratio = rawCanvas.width / rawCanvas.height
    let drawW = availW
    let drawH = availW / ratio
    if (drawH > availH) {
      drawH = availH
      drawW = availH * ratio
    }
    const dx = (A4.w - drawW) / 2
    const dy = margin // top-aligned (was vertically-centered)
    ctx.drawImage(rawCanvas, dx, dy, drawW, drawH)
    return out
  }

  const handleComparisonImage = async () => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    flashToast('Generating A4 comparison image…')
    try {
      const starter = buildPackageView('Starter')
      const basic = buildPackageView('Basic')
      const premium = buildPackageView('Premium')
      // Off-screen render at A4-portrait width (~595mm × 1pt/mm → ~794px @ 96dpi)
      const raw = await captureCustomOffscreen(
        <ComparisonQuote
          event={event}
          starter={starter}
          basic={basic}
          premium={premium}
          quoteNo={quoteNo}
          recommendedPackage={recommendedPackage}
        />,
        780,
      )
      const canvas = fitToA4(raw, 'portrait')
      canvas.toBlob((blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = buildFilename(null, 'client', 'png')
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 4000)
        flashToast('Comparison image downloaded')
      }, 'image/png')
    } catch (e) {
      console.error(e)
      flashToast('Comparison image failed')
    }
  }

  const handleComparisonPdf = async () => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    flashToast('Generating comparison PDF…')
    try {
      const starter = buildPackageView('Starter')
      const basic = buildPackageView('Basic')
      const premium = buildPackageView('Premium')
      const raw = await captureCustomOffscreen(
        <ComparisonQuote
          event={event}
          starter={starter}
          basic={basic}
          premium={premium}
          quoteNo={quoteNo}
          recommendedPackage={recommendedPackage}
        />,
        780,
      )
      const canvas = fitToA4(raw, 'portrait')
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const margin = 8
      const availW = pdfWidth - margin * 2
      const availH = pdfHeight - margin * 2
      const ratio = canvas.width / canvas.height
      let w = availW
      let h = availW / ratio
      if (h > availH) {
        h = availH
        w = availH * ratio
      }
      const x = (pdfWidth - w) / 2
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, margin, w, h, undefined, 'FAST')
      pdf.save(buildFilename(null, 'client', 'pdf'))
      flashToast('Comparison PDF downloaded')
    } catch (e) {
      console.error(e)
      flashToast('Comparison PDF failed')
    }
  }

  const handleAllPackages = async (mode) => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setShowErrors(false)

    const packages = ['Starter', 'Basic', 'Premium', 'Custom']
    const safeName = (event.guestName || 'Customer')
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32)
    const modeLabel = mode === 'client' ? 'Client' : 'Internal'

    flashToast(`Generating 4 ${modeLabel} package images…`)

    // ── Phase 1: capture every package to a Blob first ───────────
    // (Don't trigger any downloads yet — gather all the data.)
    const ready = []
    let i = 0
    for (const pkg of packages) {
      i++
      try {
        flashToast(`(${i}/4) Rendering ${pkg}…`)
        const { canvas } = await captureOffscreen(pkg, mode)
        const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'))
        if (!blob) continue
        const parts = [modeLabel]
        if (event.date) parts.push(event.date)
        if (event.serviceType) parts.push(event.serviceType)
        parts.push(pkg)
        parts.push(safeName || 'Quote')
        parts.push(quoteNo)
        ready.push({ blob, filename: parts.join('_') + '.png' })
      } catch (e) {
        console.error('Package capture failed for', pkg, e)
      }
    }

    if (!ready.length) {
      flashToast('Image capture failed')
      return
    }

    // ── Phase 2: trigger all downloads in rapid succession ───────
    // Browsers prompt once for "multiple downloads"; user allows → all 4 land.
    flashToast(`Downloading ${ready.length} image${ready.length > 1 ? 's' : ''}…`)
    for (const { blob, filename } of ready) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
      // Yield to the event loop so each click is processed but not delayed
      await new Promise((r) => setTimeout(r, 80))
    }

    flashToast(`Downloaded ${ready.length} of 4 — allow if browser asks`)
  }

  const buildFilename = (pkg, mode, ext) => {
    const safeName = (event.guestName || 'Customer')
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32)
    const modeLabel = mode === 'client' ? 'Client' : 'Internal'
    const parts = [modeLabel]
    if (event.date) parts.push(event.date)
    if (event.serviceType) parts.push(event.serviceType)
    if (pkg) parts.push(pkg) // omit when null (e.g. multi-package comparison)
    parts.push(safeName || 'Quote')
    parts.push(quoteNo)
    return parts.join('_') + '.' + ext
  }

  const handleSinglePackageImage = async (pkg, mode) => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      return
    }
    flashToast(`Rendering ${pkg}…`)
    try {
      const { canvas } = await captureOffscreen(pkg, mode)
      canvas.toBlob((blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = buildFilename(pkg, mode, 'png')
        document.body.appendChild(a)
        a.click()
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 4000)
        flashToast(`${pkg} image downloaded`)
      }, 'image/png')
    } catch (e) {
      console.error(e)
      flashToast('Image capture failed')
    }
  }

  const handleSinglePackagePdf = async (pkg, mode) => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      return
    }
    flashToast(`Rendering ${pkg}…`)
    try {
      const { canvas } = await captureOffscreen(pkg, mode)
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const margin = 8
      const availW = pdfWidth - margin * 2
      const availH = pdfHeight - margin * 2
      const imgRatio = canvas.width / canvas.height
      let imgW = availW
      let imgH = availW / imgRatio
      if (imgH > availH) {
        imgH = availH
        imgW = availH * imgRatio
      }
      const x = (pdfWidth - imgW) / 2
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, margin, imgW, imgH, undefined, 'FAST')
      pdf.save(buildFilename(pkg, mode, 'pdf'))
      flashToast(`${pkg} PDF downloaded`)
    } catch (e) {
      console.error(e)
      flashToast('PDF generation failed')
    }
  }

  const handleAllPackagesPdf = async (mode) => {
    if (hasErrors) {
      setShowErrors(true)
      setEventOpen(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setShowErrors(false)

    const packages = ['Starter', 'Basic', 'Premium', 'Custom']
    const safeName = (event.guestName || 'Customer')
      .toString()
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 32)
    const modeLabel = mode === 'client' ? 'Client' : 'Internal'

    flashToast(`Generating 4 ${modeLabel} package PDFs…`)

    const { jsPDF } = await import('jspdf')

    // Phase 1: capture & build each PDF Blob
    const ready = []
    let i = 0
    for (const pkg of packages) {
      i++
      try {
        flashToast(`(${i}/4) Rendering ${pkg}…`)
        const { canvas } = await captureOffscreen(pkg, mode)
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = pdf.internal.pageSize.getHeight()
        const margin = 8
        const availW = pdfWidth - margin * 2
        const availH = pdfHeight - margin * 2
        const imgRatio = canvas.width / canvas.height
        let imgW = availW
        let imgH = availW / imgRatio
        if (imgH > availH) {
          imgH = availH
          imgW = availH * imgRatio
        }
        const x = (pdfWidth - imgW) / 2
        const y = margin
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          x, y, imgW, imgH,
          undefined,
          'FAST',
        )
        const blob = pdf.output('blob')
        const parts = [modeLabel]
        if (event.date) parts.push(event.date)
        if (event.serviceType) parts.push(event.serviceType)
        parts.push(pkg)
        parts.push(safeName || 'Quote')
        parts.push(quoteNo)
        ready.push({ blob, filename: parts.join('_') + '.pdf' })
      } catch (e) {
        console.error('Package PDF failed for', pkg, e)
      }
    }

    if (!ready.length) {
      flashToast('PDF generation failed')
      return
    }

    flashToast(`Downloading ${ready.length} PDF${ready.length > 1 ? 's' : ''}…`)
    for (const { blob, filename } of ready) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 4000)
      await new Promise((r) => setTimeout(r, 80))
    }

    flashToast(`Downloaded ${ready.length} of 4 — allow if browser asks`)
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800 pb-44">
      {/* ── App bar ─────────────────────────────────────── */}
      <header className="no-print sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-3 h-14 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 min-w-0 flex-shrink-0">
            <img src="/assets/images/logo.png" className="h-8 w-auto" alt="" />
            <div className="min-w-0">
              <div className="font-bold text-[14px] leading-tight truncate">Quote Builder</div>
              <div className="text-[10px] text-slate-500 leading-tight truncate flex items-center gap-1.5">
                <span>{quoteNo}</span>
                {justSaved && (
                  <span className="text-emerald-600 font-semibold">
                    <i className="fas fa-check-circle" /> Saved
                  </span>
                )}
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => setSavedListOpen(true)}
              className="relative h-9 px-2.5 rounded-lg border border-slate-200 text-slate-700 text-[12px] font-semibold active:bg-slate-100 flex items-center gap-1.5"
              aria-label="Saved quotes"
            >
              <i className="fas fa-folder-open" />
              <span className="hidden xs:inline">Quotes</span>
              {savedQuotes.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#1abc9c] text-white text-[10px] font-bold grid place-items-center">
                  {savedQuotes.length}
                </span>
              )}
            </button>
            <button
              onClick={newQuote}
              className="h-9 px-2.5 rounded-lg bg-slate-900 text-white text-[12px] font-semibold active:bg-slate-700 flex items-center gap-1.5"
            >
              <i className="fas fa-plus" />
              <span>New</span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Saved Quotes Drawer (slides down from top, via portal) ─── */}
      {savedListOpen && createPortal(
        <div
          className="no-print"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 50,
          }}
        >
          <div
            onClick={() => setSavedListOpen(false)}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />
          <div className="relative w-full max-w-2xl mx-auto bg-white rounded-b-2xl shadow-2xl max-h-[88vh] flex flex-col">
            <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-slate-200 gap-2">
              <div className="flex items-baseline gap-2 min-w-0">
                <i className="fas fa-folder-open text-[#1abc9c]" />
                <span className="text-[15px] font-bold text-slate-800">
                  Saved Quotes
                </span>
                <span className="text-[11px] text-slate-500">
                  {savedQuotes.length}
                </span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => importInputRef.current?.click()}
                  className="h-9 px-3 rounded-lg bg-slate-100 text-slate-700 text-[12px] font-semibold active:bg-slate-200"
                >
                  <i className="fas fa-file-import mr-1" />
                  Import
                </button>
                <button
                  onClick={() => setSavedListOpen(false)}
                  aria-label="Close"
                  className="w-9 h-9 grid place-items-center rounded-full text-slate-500 active:bg-slate-100"
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {savedQuotes.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-slate-100 grid place-items-center">
                    <i className="fas fa-folder-open text-xl" />
                  </div>
                  <div className="text-[14px] font-semibold text-slate-600">
                    No saved quotes yet
                  </div>
                  <div className="text-[12px] mt-1">
                    Quotes auto-save once you fill the customer name
                    <br />
                    and add at least one item.
                  </div>
                </div>
              ) : (
                enrichedSavedQuotes.map((q) => {
                  const isCurrent = q.quoteNo === quoteNo
                  const totals = q.packageTotals
                  return (
                    <div
                      key={q.quoteNo}
                      className={`p-3 rounded-xl border-2 ${
                        isCurrent
                          ? 'bg-[#1abc9c]/5 border-[#1abc9c]'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="text-[10px] font-mono text-slate-500">
                              {q.quoteNo}
                            </div>
                            {isCurrent && (
                              <span className="text-[9px] font-bold uppercase tracking-wider text-[#16a085] bg-[#1abc9c]/15 px-1.5 py-0.5 rounded">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="font-bold text-[14px] truncate">
                            {q.customerName || '—'}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {q.serviceType} · {q.eventType} · {q.guests} guests
                          </div>
                          {(q.eventDate || q.startTime || q.location) && (
                            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5 truncate">
                              {q.eventDate && (
                                <>
                                  <i className="fas fa-calendar-day text-[9px] text-slate-400" />
                                  <span>
                                    {new Date(q.eventDate).toLocaleDateString(
                                      'en-IN',
                                      {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                      },
                                    )}
                                  </span>
                                </>
                              )}
                              {(q.startTime || q.endTime) && (
                                <span className="text-slate-400">
                                  · {fmtTimeRange(q.startTime, q.endTime)}
                                </span>
                              )}
                              {q.location && (
                                <>
                                  <span className="text-slate-400">·</span>
                                  <i className="fas fa-map-marker-alt text-[9px] text-slate-400" />
                                  <span className="truncate">{q.location}</span>
                                </>
                              )}
                            </div>
                          )}
                          <div className="text-[10px] text-slate-400 mt-1">
                            Saved {new Date(q.savedAt).toLocaleString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                      </div>

                      {/* All 4 package totals at-a-glance */}
                      {totals && (
                        <div className="mt-2.5 grid grid-cols-4 gap-1.5">
                          {['Starter', 'Basic', 'Premium', 'Custom'].map(
                            (pkg) => (
                              <div
                                key={pkg}
                                className="px-2 py-1.5 rounded-lg bg-slate-50"
                              >
                                <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">
                                  {pkg}
                                </div>
                                <div className="text-[12px] font-bold leading-tight mt-0.5 truncate text-slate-800">
                                  {fmtINR(totals[pkg])}
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                      <div className="mt-2.5 flex items-stretch gap-1.5">
                        <button
                          onClick={() => loadQuote(q.quoteNo)}
                          disabled={isCurrent}
                          className="flex-1 min-w-0 h-9 rounded-lg bg-[#1abc9c] text-white text-[12px] font-bold active:bg-[#16a085] disabled:bg-slate-200 disabled:text-slate-400 inline-flex items-center justify-center gap-1.5"
                        >
                          <i className="fas fa-folder-open" />
                          <span>{isCurrent ? 'Loaded' : 'Load'}</span>
                        </button>
                        <button
                          onClick={() => cloneQuote(q.quoteNo)}
                          title="Clone this quote (items + charges, blank customer)"
                          className="flex-shrink-0 h-9 px-2.5 rounded-lg bg-sky-50 text-sky-700 text-[12px] font-bold active:bg-sky-100 inline-flex items-center justify-center gap-1"
                        >
                          <i className="fas fa-clone" />
                          <span>Clone</span>
                        </button>
                        <button
                          onClick={() => openShare(q.quoteNo)}
                          title="Share this quote"
                          aria-label="Share"
                          className="flex-shrink-0 h-9 w-9 rounded-lg bg-violet-50 text-violet-700 text-[12px] font-bold active:bg-violet-100 inline-flex items-center justify-center"
                        >
                          <i className="fas fa-share-alt" />
                        </button>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete quote ${q.quoteNo}? This can't be undone.`,
                              )
                            )
                              deleteQuote(q.quoteNo)
                          }}
                          aria-label="Delete"
                          className="flex-shrink-0 h-9 w-9 rounded-lg bg-rose-50 text-rose-600 text-[12px] font-bold active:bg-rose-100 inline-flex items-center justify-center"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      <main className="no-print max-w-2xl mx-auto px-3 pt-3 space-y-3">
        {/* ── Event card (collapsible) ─────────────────── */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <button
            onClick={() => setEventOpen((v) => !v)}
            className="w-full flex items-center gap-3 p-4 active:bg-slate-50"
          >
            <div
              style={{ width: 40, height: 40, flexShrink: 0 }}
              className="grid place-items-center rounded-full bg-emerald-100 text-[#0f766e]"
            >
              <i className="fas fa-calendar-day" />
            </div>
            <div
              className="text-left"
              style={{ flex: '1 1 0%', minWidth: 0 }}
            >
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                Event
              </div>
              <div className="text-[14px] font-semibold text-slate-800 truncate">
                {eventSummary(event) || 'Tap to fill event details'}
              </div>
              {eventLineTwo(event) && (
                <div className="text-[11px] text-slate-500 truncate">
                  {eventLineTwo(event)}
                </div>
              )}
            </div>
            <i
              style={{ flexShrink: 0 }}
              className={`fas fa-chevron-${eventOpen ? 'up' : 'down'} text-slate-400`}
            />
          </button>

          {eventOpen && (
            <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
              {showErrors && hasErrors && (
                <div className="px-3 py-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-[13px] flex items-start gap-2">
                  <i className="fas fa-exclamation-circle mt-0.5" />
                  <span>Please fill the required fields marked <span className="font-bold">*</span> before generating the PDF.</span>
                </div>
              )}

              <Field label="Service Type">
                <div className="grid grid-cols-2 gap-1.5">
                  {SERVICE_TYPES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setEvent({ ...event, serviceType: s })}
                      className={`h-12 rounded-xl text-[13px] font-bold uppercase tracking-wide border-2 transition flex items-center justify-center gap-2 ${
                        event.serviceType === s
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 border-slate-200 active:bg-slate-50'
                      }`}
                    >
                      <i
                        className={`fas ${
                          s === 'Catering' ? 'fa-utensils' : 'fa-camera'
                        } text-[12px]`}
                      />
                      {s}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Package">
                <div className="grid grid-cols-4 gap-1.5">
                  {PACKAGES.map((p) => (
                    <button
                      key={p}
                      onClick={() => selectPackage(p)}
                      className={`h-11 rounded-xl text-[12px] font-bold uppercase tracking-wide border-2 transition ${
                        event.packageName === p
                          ? 'bg-[#1abc9c] text-white border-[#1abc9c]'
                          : 'bg-white text-slate-600 border-slate-200 active:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </Field>

              <Field
                label="Customer Name"
                required
                error={showErrors && errors.guestName}
              >
                <div className="flex gap-2">
                  <select
                    className="input"
                    style={{ width: '92px', flexShrink: 0 }}
                    value={event.salutation || ''}
                    onChange={(e) =>
                      setEvent({ ...event, salutation: e.target.value })
                    }
                    aria-label="Salutation"
                  >
                    {SALUTATIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <input
                    className={`input ${showErrors && errors.guestName ? 'input-error' : ''}`}
                    value={event.guestName}
                    onChange={(e) =>
                      setEvent({ ...event, guestName: e.target.value })
                    }
                    placeholder="Karthik"
                  />
                </div>
              </Field>

              <Field
                label="Contact"
                required
                error={showErrors && errors.contact}
              >
                <div className="flex gap-2">
                  <select
                    className="input"
                    style={{ width: '96px', flexShrink: 0 }}
                    value={event.contactCode || '+91'}
                    onChange={(e) =>
                      setEvent({ ...event, contactCode: e.target.value })
                    }
                    aria-label="Country code"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <input
                    className={`input ${showErrors && errors.contact ? 'input-error' : ''}`}
                    inputMode="numeric"
                    type="tel"
                    maxLength={phoneMax}
                    value={event.contact}
                    onChange={(e) =>
                      setEvent({
                        ...event,
                        contact: e.target.value.replace(/\D/g, '').slice(0, phoneMax),
                      })
                    }
                    placeholder={isIndiaCode ? '9444848616' : 'phone number'}
                  />
                </div>
                {contactDigits.length > 0 &&
                  (contactDigits.length < phoneMin ||
                    contactDigits.length > phoneMax) && (
                    <div className="text-[11px] text-rose-600 mt-1">
                      {isIndiaCode
                        ? `Phone must be exactly 10 digits (${contactDigits.length} entered)`
                        : `Phone must be ${phoneMin}–${phoneMax} digits`}
                    </div>
                  )}
              </Field>

              <Field label="No. of Guests">
                <Stepper
                  value={event.guestCount}
                  onChange={(v) => setEvent({ ...event, guestCount: v })}
                />
              </Field>

              <Field
                label="Event Type"
                required
                error={showErrors && errors.eventType}
              >
                <select
                  className={`input ${showErrors && errors.eventType ? 'input-error' : ''}`}
                  value={event.eventType}
                  onChange={(e) => setEvent({ ...event, eventType: e.target.value })}
                >
                  {EVENT_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </Field>

              <Field label="Date">
                <input
                  type="date"
                  className="input"
                  value={event.date}
                  onChange={(e) => setEvent({ ...event, date: e.target.value })}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start Time">
                  <input
                    type="time"
                    className="input"
                    value={event.startTime || ''}
                    onChange={(e) =>
                      setEvent({ ...event, startTime: e.target.value })
                    }
                  />
                </Field>
                <Field label="End Time">
                  <input
                    type="time"
                    className="input"
                    value={event.endTime || ''}
                    onChange={(e) =>
                      setEvent({ ...event, endTime: e.target.value })
                    }
                  />
                </Field>
              </div>

              <Field
                label="Location"
                required
                error={showErrors && errors.location}
              >
                <input
                  className={`input ${showErrors && errors.location ? 'input-error' : ''}`}
                  value={event.location}
                  onChange={(e) =>
                    setEvent({ ...event, location: e.target.value })
                  }
                  placeholder="Venue / Address"
                />
              </Field>
            </div>
          )}
        </section>

        {/* ── Filter chips (sticky under app bar) ─────── */}
        <div className="sticky top-14 z-30 -mx-3 px-3 py-2 bg-slate-100/95 backdrop-blur supports-[backdrop-filter]:bg-slate-100/70">
          <div className="flex items-center gap-2">
            <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1 min-w-0">
              {['All', ...availableCategories].map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={`px-3.5 h-9 text-[13px] rounded-full border whitespace-nowrap flex-shrink-0 ${
                    filter === c
                      ? 'bg-slate-900 text-white border-slate-900 font-semibold'
                      : 'bg-white text-slate-600 border-slate-300'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `Reset items in ${currentPkgName} package to default template? Your current selections in this package will be replaced.`,
                  )
                ) {
                  resetCurrentPackageToPreset()
                }
              }}
              title={`Reset ${currentPkgName} items to default`}
              className="flex-shrink-0 h-9 px-3 rounded-full bg-amber-100 text-amber-800 text-[12px] font-semibold active:bg-amber-200 inline-flex items-center gap-1"
            >
              <i className="fas fa-rotate-left text-[11px]" />
              Reset
            </button>
          </div>
        </div>

        {/* ── Items list ─────────────────────────────── */}
        <section className="space-y-2">
          {visibleCatalog.length === 0 && visibleCustomItems.length === 0 && (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-6 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 grid place-items-center text-slate-400 mb-3">
                <i className="fas fa-box-open" />
              </div>
              <div className="font-semibold text-slate-700 text-[14px]">
                No {event.serviceType} items yet
              </div>
              <div className="text-[12px] text-slate-500 mt-1">
                Inventory for {event.serviceType} will be added soon.
                <br />
                You can still build a quote using <b>Add Custom Item</b> below.
              </div>
            </div>
          )}

          {visibleCatalog.map((item, i) => {
            const prevItem = visibleCatalog[i - 1]
            const showCatHeader =
              filter === 'All' && (!prevItem || prevItem.category !== item.category)

            return (
              <React.Fragment key={item.id}>
                {showCatHeader && (
                  <div className="px-1 pt-2 pb-1 text-[11px] uppercase tracking-wider font-bold text-slate-500">
                    {item.category}
                  </div>
                )}
                <ItemCard
                  item={item}
                  guests={event.guestCount}
                  packageName={event.packageName}
                  selected={selected[item.id] !== undefined}
                  override={selected[item.id] || {}}
                  editing={!!editing[item.id]}
                  onToggle={() => toggleItem(item)}
                  onEditToggle={() =>
                    setEditing((e) => ({ ...e, [item.id]: !e[item.id] }))
                  }
                  onChange={(k, v) => updateOverride(item.id, k, v)}
                  onReset={(k) => resetOverride(item.id, k)}
                />
              </React.Fragment>
            )
          })}

          {visibleCustomItems.length > 0 && (
            <div className="px-1 pt-3 pb-1 text-[11px] uppercase tracking-wider font-bold text-slate-500">
              Custom
            </div>
          )}
          {visibleCustomItems.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              guests={event.guestCount}
              packageName={event.packageName}
              selected={true}
              override={selected[item.id] || {}}
              editing={!!editing[item.id]}
              onToggle={() => removeCustomItem(item.id)}
              onEditToggle={() =>
                setEditing((e) => ({ ...e, [item.id]: !e[item.id] }))
              }
              onChange={(k, v) => updateOverride(item.id, k, v)}
              onChangeMeta={(k, v) => updateCustomField(item.id, k, v)}
              onReset={(k) => resetOverride(item.id, k)}
            />
          ))}

          <button
            onClick={addCustomItem}
            className="w-full py-3.5 mt-2 rounded-2xl bg-white border-2 border-dashed border-slate-300 text-slate-600 font-semibold text-sm active:bg-slate-50"
          >
            <i className="fas fa-plus mr-2" /> Add Custom Item
          </button>
        </section>

        <div className="h-2" />
      </main>

      {/* ── Bottom sheet ───────────────────────────────── */}
      <div className="no-print fixed bottom-0 inset-x-0 z-40">
        <div
          className={`mx-auto max-w-2xl bg-white border-t border-slate-200 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] rounded-t-2xl transition-[max-height] duration-300 overflow-hidden ${
            sheetOpen ? 'max-h-[80vh]' : 'max-h-[148px]'
          }`}
        >
          {/* Drag handle — bigger tap target */}
          <button
            type="button"
            onClick={() => setSheetOpen((v) => !v)}
            className="w-full pt-3 pb-2 flex flex-col items-center active:bg-slate-50"
            aria-label={sheetOpen ? 'Collapse' : 'Expand details'}
          >
            <span className="block w-12 h-1.5 rounded-full bg-slate-300" />
          </button>

          {/* COLLAPSED VIEW — Grand Total + Edit + PDF buttons (always visible) */}
          <div className="px-4 pt-1">
            <div className="flex items-end justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400">
                  Grand Total
                </div>
                <div className="text-2xl font-extrabold text-[#16a085] leading-tight">
                  {fmtINR(grandTotal)}
                </div>
              </div>
              <button
                onClick={() => setSheetOpen((v) => !v)}
                className="h-9 px-3 rounded-lg bg-slate-100 text-slate-700 text-[12px] font-semibold active:bg-slate-200"
              >
                <i className={`fas fa-chevron-${sheetOpen ? 'down' : 'up'} mr-1`} />
                {sheetOpen ? 'Hide' : 'Edit charges'}
              </button>
            </div>
          </div>

          <div className="px-4 pb-3">
            <button
              onClick={() => {
                if (hasErrors) {
                  setShowErrors(true)
                  setEventOpen(true)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                  return
                }
                setPreviewOpen(true)
              }}
              disabled={lines.length === 0 && customItems.length === 0}
              className="w-full h-12 rounded-xl bg-violet-600 text-white font-bold text-[13.5px] active:bg-violet-700 disabled:bg-slate-200 disabled:text-slate-400 inline-flex items-center justify-center gap-2"
            >
              <i className="fas fa-eye" />
              Preview &amp; Download
            </button>
          </div>

          {/* EXPANDED VIEW — breakdown + Travel/Profit/Round-Off inputs */}
          <div className="px-4 pb-5 pt-3 border-t border-slate-100 space-y-3">
            {/* Breakdown rows */}
            <div className="text-[12.5px] space-y-0.5">
              <div className="flex items-center justify-between py-0.5">
                <span className="text-slate-600">Items Subtotal</span>
                <span className="font-semibold">{fmtINR(itemsSubtotal)}</span>
              </div>
              {Number(travelCharge) > 0 && (
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-slate-600">+ Travel</span>
                  <span className="font-semibold">{fmtINR(travelCharge)}</span>
                </div>
              )}
              {Number(overallProfitPct) > 0 && (
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-slate-600">
                    + Profit ({overallProfitPct}%)
                  </span>
                  <span className="font-semibold">{fmtINR(overallProfit)}</span>
                </div>
              )}
              {Number(roundOff) !== 0 && (
                <div className="flex items-center justify-between py-0.5">
                  <span className="text-slate-600">
                    {roundOff > 0 ? '+' : '−'} Round Off
                  </span>
                  <span className="font-semibold">{fmtINR(roundOff)}</span>
                </div>
              )}
              {totalProfit !== 0 && (
                <div className="flex items-center justify-between py-0.5 px-1.5 rounded bg-amber-50 text-amber-800">
                  <span className="font-semibold">
                    Total Profit ({totalProfitPct}%)
                  </span>
                  <span className="font-bold">{fmtINR(totalProfit)}</span>
                </div>
              )}
              <div className="h-px bg-slate-200 my-1" />
              <div className="flex items-center justify-between py-0.5">
                <span className="font-bold text-slate-800">Grand Total</span>
                <span className="font-extrabold text-[#16a085]">
                  {fmtINR(grandTotal)}
                </span>
              </div>
            </div>

            <div className="px-2.5 py-1.5 rounded-lg bg-violet-50 text-violet-700 text-[11px] font-semibold flex items-center gap-1.5">
              <i className="fas fa-info-circle" />
              Editing charges for <b className="uppercase">{currentPkgName}</b>
              <span className="text-violet-400">·</span>
              <span className="font-normal text-violet-500">
                each package has its own
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Travel ₹">
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className="input"
                  placeholder="0"
                  value={showNum(travelCharge)}
                  onChange={(e) => setTravelCharge(toNum(e.target.value))}
                />
              </Field>
              <Field label="Overall Profit %">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.5"
                  className="input"
                  placeholder="0"
                  value={showNum(overallProfitPct)}
                  onChange={(e) => setOverallProfitPct(toNum(e.target.value))}
                />
              </Field>
            </div>

            <Field label="Round Off ₹">
              <input
                type="number"
                inputMode="numeric"
                className="input"
                placeholder="0"
                value={showNum(roundOff)}
                onChange={(e) => {
                  setRoundOff(toNum(e.target.value))
                  setRoundMode('manual')
                }}
              />
              <div className="grid grid-cols-5 gap-1 mt-2">
                {[
                  { key: '500', label: '₹500' },
                  { key: '1000', label: '₹1K' },
                  { key: '2500', label: '₹2.5K' },
                  { key: '5000', label: '₹5K' },
                  { key: '10000', label: '₹10K' },
                ].map((opt) => {
                  const active = roundMode === opt.key
                  return (
                    <button
                      key={opt.key}
                      onClick={() => setRoundMode(opt.key)}
                      title={`Round to nearest ${opt.label}`}
                      className={`h-9 rounded-lg text-[12px] font-semibold border-2 transition ${
                        active
                          ? 'bg-[#1abc9c] text-white border-[#1abc9c]'
                          : 'bg-white text-slate-600 border-slate-200 active:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
              {roundMode === 'manual' && (
                <div className="mt-1.5 flex items-center justify-between text-[10.5px]">
                  <span className="text-slate-500 italic">
                    Manual round-off
                  </span>
                  <button
                    onClick={() => setRoundMode('5000')}
                    className="text-[#16a085] font-semibold active:opacity-70"
                  >
                    ↻ Use auto (₹5K)
                  </button>
                </div>
              )}
            </Field>
          </div>
        </div>
      </div>

      {/* ── Printable view ───────────────────────────── */}
      <div className="print-area">
        {pdfMode === 'client' ? (
          <ClientQuote
            event={event}
            lines={lines}
            grandTotal={grandTotal}
            quoteNo={quoteNo}
          />
        ) : (
          <InternalQuote
            event={event}
            lines={lines}
            itemsSubtotal={itemsSubtotal}
            travelCharge={Number(travelCharge) || 0}
            overallProfitPct={Number(overallProfitPct) || 0}
            overallProfit={overallProfit}
            roundOff={Number(roundOff) || 0}
            totalProfit={totalProfit}
            totalProfitPct={totalProfitPct}
            grandTotal={grandTotal}
            quoteNo={quoteNo}
          />
        )}
      </div>

      {/* Hidden file input for Import */}
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) importFromFile(f)
          e.target.value = ''
        }}
      />

      {/* Preview & Compare 4 Packages */}
      {previewOpen &&
        createPortal(
          <div
            className="no-print"
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 60,
            }}
          >
            <div
              onClick={() => setPreviewOpen(false)}
              className="absolute inset-0 bg-slate-900/70"
            />
            <div className="relative w-full max-w-3xl mx-auto bg-slate-50 h-[96vh] mt-[2vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-violet-600">
                    Compare
                  </div>
                  <div className="text-[15px] font-bold text-slate-800 truncate">
                    All 4 Packages
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Mode toggle */}
                  <div className="flex bg-slate-100 rounded-lg p-0.5 text-[11px] font-bold">
                    <button
                      onClick={() => setPreviewMode('client')}
                      className={`px-3 h-7 rounded-md ${
                        previewMode === 'client'
                          ? 'bg-white text-[#0f766e] shadow-sm'
                          : 'text-slate-500'
                      }`}
                    >
                      Client
                    </button>
                    <button
                      onClick={() => setPreviewMode('internal')}
                      className={`px-3 h-7 rounded-md ${
                        previewMode === 'internal'
                          ? 'bg-white text-slate-800 shadow-sm'
                          : 'text-slate-500'
                      }`}
                    >
                      Internal
                    </button>
                  </div>
                  <button
                    onClick={() => setPreviewOpen(false)}
                    aria-label="Close"
                    className="w-9 h-9 grid place-items-center rounded-full text-slate-500 active:bg-slate-100"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              </div>

              {/* Modal body — stacked package cards */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {['Starter', 'Basic', 'Premium', 'Custom'].map((pkg) => {
                  const v = buildPackageView(pkg)
                  const showInternalDetail = previewMode === 'internal'
                  return (
                    <div
                      key={pkg}
                      className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
                    >
                      {/* Package header */}
                      <div className="flex items-end justify-between px-4 py-3 border-b border-slate-100 gap-2">
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1.5">
                            Package
                            {recommendedPackage === pkg && (
                              <span className="text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                ★ Recommended
                              </span>
                            )}
                          </div>
                          <div className="text-[18px] font-extrabold text-slate-800">
                            {pkg}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {v.pkgLines.length} item
                            {v.pkgLines.length !== 1 && 's'} ·{' '}
                            {event.guestCount} guests
                          </div>
                          <button
                            onClick={() =>
                              setRecommendedPackage(
                                recommendedPackage === pkg ? null : pkg,
                              )
                            }
                            className={`mt-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                              recommendedPackage === pkg
                                ? 'bg-amber-500 text-white active:bg-amber-600'
                                : 'bg-amber-50 text-amber-700 border border-amber-200 active:bg-amber-100'
                            }`}
                          >
                            <i
                              className={`fas ${
                                recommendedPackage === pkg ? 'fa-star' : 'fa-star'
                              } text-[10px]`}
                            />
                            {recommendedPackage === pkg
                              ? 'Recommended'
                              : 'Mark as recommended'}
                          </button>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400">
                            Grand Total
                          </div>
                          <div className="text-[22px] font-extrabold text-[#16a085] leading-tight">
                            {fmtINR(v.pkgGrandTotal)}
                          </div>
                        </div>
                      </div>

                      {/* Items list */}
                      {v.pkgLines.length === 0 ? (
                        <div className="text-center text-slate-400 italic py-6 text-[12px]">
                          No items in this package
                        </div>
                      ) : (
                        <ul className="divide-y divide-slate-100">
                          {v.pkgLines.map((line, i) => {
                            const { item, override, qty, unitLabel, total } =
                              line
                            const isInclusive = item.rule === 'inclusive'
                            const showQty = override.showQty === true
                            return (
                              <li
                                key={item.id}
                                className="flex items-start justify-between gap-3 px-4 py-2"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="text-[12.5px] font-semibold text-slate-800">
                                    <span className="text-slate-400 font-mono mr-1.5">
                                      {String(i + 1).padStart(2, '0')}
                                    </span>
                                    {item.name}
                                    {showInternalDetail && !isInclusive && (
                                      <span className="text-slate-500 font-normal ml-1.5">
                                        — {qty} {unitLabel}
                                      </span>
                                    )}
                                    {!showInternalDetail && showQty && !isInclusive && (
                                      <span className="text-slate-500 font-normal ml-1.5">
                                        ({qty} {unitLabel})
                                      </span>
                                    )}
                                  </div>
                                  {item.description && (
                                    <div
                                      className="text-[10.5px] text-slate-500 italic mt-0.5"
                                      style={{ whiteSpace: 'pre-line' }}
                                    >
                                      {item.description}
                                    </div>
                                  )}
                                </div>
                                {showInternalDetail && (
                                  <div className="text-right flex-shrink-0">
                                    <div className="text-[12.5px] font-bold text-slate-800">
                                      {isInclusive ? (
                                        <span className="text-emerald-600 text-[10px] font-bold">
                                          INCL.
                                        </span>
                                      ) : (
                                        fmtINR(total)
                                      )}
                                    </div>
                                  </div>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      )}

                      {/* Internal-mode breakdown (profit + total profit %) */}
                      {previewMode === 'internal' && (
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-[12px] space-y-1">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Items Subtotal</span>
                            <span className="font-semibold">
                              {fmtINR(v.pkgItemsSubtotal)}
                            </span>
                          </div>
                          {Number(v.pkgTravel) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">+ Travel</span>
                              <span className="font-semibold">
                                {fmtINR(v.pkgTravel)}
                              </span>
                            </div>
                          )}
                          {Number(v.pkgProfitPct) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">
                                + Profit ({v.pkgProfitPct}%)
                              </span>
                              <span className="font-semibold">
                                {fmtINR(v.pkgProfit)}
                              </span>
                            </div>
                          )}
                          {v.pkgRoundOff !== 0 && (
                            <div className="flex justify-between">
                              <span className="text-slate-600">
                                {v.pkgRoundOff > 0 ? '+' : '−'} Round Off
                              </span>
                              <span className="font-semibold">
                                {fmtINR(v.pkgRoundOff)}
                              </span>
                            </div>
                          )}
                          {v.pkgTotalProfit !== 0 && (
                            <div className="flex justify-between items-center bg-amber-100 px-2 py-1 rounded -mx-1 mt-1">
                              <span className="font-bold text-amber-900">
                                Total Profit ({v.pkgTotalProfitPct}%)
                              </span>
                              <span className="font-extrabold text-amber-900">
                                {fmtINR(v.pkgTotalProfit)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Per-package download buttons */}
                      <div className="px-4 py-3 bg-slate-50 grid grid-cols-2 gap-2 border-t border-slate-100">
                        <button
                          onClick={() =>
                            handleSinglePackageImage(pkg, previewMode)
                          }
                          className={`h-10 rounded-lg ${
                            previewMode === 'client'
                              ? 'bg-[#1abc9c] active:bg-[#16a085]'
                              : 'bg-slate-800 active:bg-slate-700'
                          } text-white font-bold text-[12px] inline-flex items-center justify-center gap-1.5`}
                        >
                          <i className="fas fa-image" /> Download Image
                        </button>
                        <button
                          onClick={() =>
                            handleSinglePackagePdf(pkg, previewMode)
                          }
                          className="h-10 rounded-lg bg-white border border-slate-300 text-slate-800 font-bold text-[12px] active:bg-slate-100 inline-flex items-center justify-center gap-1.5"
                        >
                          <i className="fas fa-file-pdf" /> Download PDF
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Modal footer — comparison + per-package download */}
              <div className="bg-white border-t border-slate-200 px-3 py-3 flex-shrink-0 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setPreviewOpen(false)
                      handleComparisonImage()
                    }}
                    className="h-11 rounded-xl bg-amber-500 text-white font-bold text-[13px] active:bg-amber-600 inline-flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-columns" /> 1 Image
                  </button>
                  <button
                    onClick={() => {
                      setPreviewOpen(false)
                      handleComparisonPdf()
                    }}
                    className="h-11 rounded-xl bg-amber-100 text-amber-800 font-bold text-[13px] active:bg-amber-200 inline-flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-file-pdf" /> 1 PDF
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      setPreviewOpen(false)
                      handleAllPackages(previewMode)
                    }}
                    className={`h-11 rounded-xl ${
                      previewMode === 'client'
                        ? 'bg-[#1abc9c] active:bg-[#16a085]'
                        : 'bg-slate-800 active:bg-slate-700'
                    } text-white font-bold text-[13px] inline-flex items-center justify-center gap-2`}
                  >
                    <i className="fas fa-images" /> Separate Images
                  </button>
                  <button
                    onClick={() => {
                      setPreviewOpen(false)
                      handleAllPackagesPdf(previewMode)
                    }}
                    className="h-11 rounded-xl bg-violet-600 text-white font-bold text-[13px] active:bg-violet-700 inline-flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-file-pdf" /> Separate PDFs
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Share modal */}
      {shareTarget &&
        createPortal(
          <div
            className="no-print"
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 60,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}
          >
            <div
              onClick={() => setShareTarget(null)}
              className="absolute inset-0 bg-slate-900/60"
            />
            <div className="relative w-full max-w-md bg-white rounded-t-2xl shadow-2xl p-4 pb-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide font-bold text-slate-400">
                    Share quote
                  </div>
                  <div className="text-[15px] font-bold text-slate-800 font-mono">
                    {shareTarget.quoteNo}
                  </div>
                </div>
                <button
                  onClick={() => setShareTarget(null)}
                  aria-label="Close"
                  className="w-9 h-9 grid place-items-center rounded-full text-slate-500 active:bg-slate-100"
                >
                  <i className="fas fa-times" />
                </button>
              </div>

              <div className="text-[11.5px] text-slate-500 mb-3">
                Anyone with the link can open this quote on any browser. The
                share link contains the full quote data — no server needed.
              </div>

              <div className="space-y-2">
                {typeof navigator !== 'undefined' && navigator.share && (
                  <button
                    onClick={doNativeShare}
                    className="w-full h-12 rounded-xl bg-[#1abc9c] text-white font-bold text-[14px] active:bg-[#16a085] flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-share-alt" /> Share via WhatsApp / Mail / …
                  </button>
                )}
                <button
                  onClick={doCopyLink}
                  className="w-full h-12 rounded-xl bg-slate-900 text-white font-bold text-[14px] active:bg-slate-700 flex items-center justify-center gap-2"
                >
                  <i className="fas fa-link" /> Copy share link
                </button>
                <button
                  onClick={doDownloadJson}
                  className="w-full h-12 rounded-xl bg-slate-100 text-slate-800 font-bold text-[14px] active:bg-slate-200 flex items-center justify-center gap-2"
                >
                  <i className="fas fa-download" /> Download JSON file
                </button>
              </div>

              <div className="mt-3 px-3 py-2 rounded-lg bg-slate-50 text-[11px] text-slate-500 font-mono break-all">
                {buildShareUrl(shareTarget.state).slice(0, 90)}
                {buildShareUrl(shareTarget.state).length > 90 && '…'}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Toast */}
      {shareToast &&
        createPortal(
          <div
            className="no-print"
            style={{
              position: 'fixed',
              left: '50%',
              bottom: '24px',
              transform: 'translateX(-50%)',
              zIndex: 70,
              background: '#0f172a',
              color: '#fff',
              padding: '10px 16px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 600,
              boxShadow: '0 8px 24px rgba(15,23,42,0.25)',
            }}
          >
            {shareToast}
          </div>,
          document.body,
        )}

      <GlobalStyles />
    </div>
  )
}

// ─── small components ────────────────────────────────────────────────────────

const Field = ({ label, required, error, children }) => (
  <label className="block">
    <div className="text-[11px] font-semibold mb-1 uppercase tracking-wide">
      <span className={error ? 'text-rose-600' : 'text-slate-500'}>{label}</span>
      {required && <span className="text-rose-500 ml-0.5">*</span>}
    </div>
    {children}
    {error && (
      <div className="text-[11px] text-rose-600 mt-1 font-medium">Required</div>
    )}
  </label>
)

const Stepper = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <button
      onClick={() => onChange(Math.max(0, Number(value) - 10))}
      className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 text-xl font-bold active:bg-slate-200"
    >
      −
    </button>
    <input
      type="number"
      inputMode="numeric"
      min="0"
      className="input text-center font-bold text-lg flex-1"
      placeholder="0"
      value={showNum(value)}
      onChange={(e) => onChange(toNum(e.target.value))}
    />
    <button
      onClick={() => onChange(Number(value) + 10)}
      className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 text-xl font-bold active:bg-slate-200"
    >
      +
    </button>
  </div>
)

const BreakdownRow = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className={bold ? 'font-bold text-base' : 'text-slate-600'}>{label}</span>
    <span className={bold ? 'font-extrabold text-[#16a085] text-lg' : 'font-semibold'}>
      {value}
    </span>
  </div>
)


// ─── item card ───────────────────────────────────────────────────────────────

const ItemCard = ({
  item,
  guests,
  packageName,
  selected,
  override,
  editing,
  onToggle,
  onEditToggle,
  onChange,
  onChangeMeta,
  onReset,
}) => {
  const calc = calcLine(item, guests, override, { packageName })
  const isInclusive = item.rule === 'inclusive'
  const showQty = override.showQty === true

  if (!selected) {
    return (
      <button
        onClick={onToggle}
        className="w-full text-left bg-white rounded-2xl border border-slate-200 p-3.5 active:bg-slate-50 active:scale-[0.99] transition"
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] leading-tight">{item.name}</div>
            {item.description && (
              <div
                className="text-[11.5px] text-slate-600 italic mt-0.5"
                style={{ whiteSpace: 'pre-line' }}
              >
                {item.description}
              </div>
            )}
            <div className="text-[11px] text-slate-500 mt-0.5">
              {isInclusive ? (
                <span className="text-emerald-600 font-semibold">Inclusive</span>
              ) : item.rule === 'flat' ? (
                <>Flat · {fmtINR(item.flatPrice)}</>
              ) : item.rule === 'qty_offset' ? (
                <>Zero-line · qty = guests + {item.offset}</>
              ) : item.rule === 'package_qty' ? (
                <>
                  {item.unitLabel} · ₹{item.rate} × {calc.qty} ·{' '}
                  <span className="text-slate-400">[{packageName}]</span>
                </>
              ) : (
                <>
                  {item.unitLabel} · ₹{item.rate?.toLocaleString('en-IN')}
                  {item.serves > 0 && ` · serves ${item.serves}/${item.unitLabel.toLowerCase()}`}
                </>
              )}
            </div>
            {item.note && (
              <div className="text-[10.5px] text-amber-700 mt-0.5">{item.note}</div>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            {!isInclusive && (
              <div className="font-bold text-[15px] text-slate-700">
                {fmtINR(calc.total)}
              </div>
            )}
            <div className="mt-1 w-9 h-9 ml-auto grid place-items-center rounded-full bg-slate-100 text-slate-500">
              <i className="fas fa-plus text-sm" />
            </div>
          </div>
        </div>
      </button>
    )
  }

  // Selected
  return (
    <div className="bg-white rounded-2xl border-2 border-[#1abc9c] shadow-sm overflow-hidden">
      <div className="p-3.5">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            {item.custom ? (
              <input
                className="input"
                placeholder="Custom item name"
                value={item.name}
                onChange={(e) => onChangeMeta('name', e.target.value)}
              />
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <i className="fas fa-check-circle text-[#1abc9c] text-[13px]" />
                  <div className="font-semibold text-[15px] leading-tight truncate">
                    {item.name}
                  </div>
                </div>
                {item.description && (
                  <div
                    className="text-[11.5px] text-slate-600 italic mt-0.5"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {item.description}
                  </div>
                )}
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                  <span>{item.category}</span>
                  {isInclusive && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
                      Inclusive
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            onClick={onToggle}
            aria-label="Remove"
            style={{ width: '36px', height: '36px', flexShrink: 0 }}
            className="grid place-items-center rounded-full text-rose-500 active:bg-rose-50"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Big total */}
        {!isInclusive && (
          <div className="mt-3 flex items-baseline justify-between">
            <div className="text-[12px] text-slate-500">
              {calc.qty} {calc.unitLabel}
              {calc.unitPrice > 0 && <> × ₹{calc.unitPrice.toLocaleString('en-IN')}</>}
            </div>
            <div className="text-xl font-extrabold text-[#16a085]">
              {fmtINR(calc.total)}
            </div>
          </div>
        )}
        {isInclusive && (
          <div className="mt-3 text-[13px] font-semibold text-emerald-600">
            Included with quote
          </div>
        )}

        {calc.surchargeNote && (
          <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
            <i className="fas fa-info-circle mr-1" />
            {calc.surchargeNote}
          </div>
        )}

        {/* Show qty toggle */}
        {!isInclusive && (
          <button
            onClick={() => onChange('showQty', !showQty)}
            className={`mt-3 w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border-2 transition ${
              showQty
                ? 'bg-[#1abc9c]/10 border-[#1abc9c]/40'
                : 'bg-slate-50 border-slate-200'
            }`}
          >
            <div className="flex items-center gap-2 text-[13px] text-left">
              <i
                className={`fas ${
                  showQty ? 'fa-check-square text-[#1abc9c]' : 'fa-square text-slate-400'
                }`}
              />
              <span className="font-medium">Print quantity on PDF</span>
            </div>
            <span
              className={`text-[10px] font-bold uppercase ${
                showQty ? 'text-[#16a085]' : 'text-slate-400'
              }`}
            >
              {showQty ? 'On' : 'Off'}
            </span>
          </button>
        )}

        {/* Customize toggle */}
        <button
          onClick={onEditToggle}
          className="mt-2 w-full py-2.5 text-[13px] font-semibold text-slate-600 active:bg-slate-100 rounded-xl flex items-center justify-center gap-1.5"
        >
          <i className={`fas ${editing ? 'fa-chevron-up' : 'fa-sliders-h'}`} />
          {editing ? 'Hide customization' : 'Customize'}
        </button>
      </div>

      {/* Edit panel */}
      {editing && (
        <div className="px-3.5 pb-4 pt-3 bg-slate-50 border-t border-slate-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Qty">
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.5"
                  className="input"
                  placeholder={String(calc.qty)}
                  value={override.qty ?? calc.qty}
                  onChange={(e) => onChange('qty', e.target.value)}
                  disabled={isInclusive}
                />
                {(override.qty !== undefined && override.qty !== '') && !item.custom && (
                  <button
                    onClick={() => onReset('qty')}
                    aria-label="Reset"
                    className="w-8 h-8 grid place-items-center rounded-md text-slate-400"
                  >
                    ↺
                  </button>
                )}
              </div>
            </Field>
            <Field label="Unit">
              {item.custom ? (
                <input
                  className="input"
                  value={item.unitLabel}
                  onChange={(e) => onChangeMeta('unitLabel', e.target.value)}
                />
              ) : (
                <div className="input bg-white text-slate-600">{calc.unitLabel}</div>
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Rate ₹">
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className="input"
                  placeholder={String(calc.unitPrice)}
                  value={override.unitPrice ?? calc.unitPrice}
                  onChange={(e) => onChange('unitPrice', e.target.value)}
                  disabled={isInclusive}
                />
                {(override.unitPrice !== undefined && override.unitPrice !== '') && !item.custom && (
                  <button
                    onClick={() => onReset('unitPrice')}
                    aria-label="Reset"
                    className="w-8 h-8 grid place-items-center rounded-md text-slate-400"
                  >
                    ↺
                  </button>
                )}
              </div>
            </Field>
            <Field label="Extra ₹">
              <input
                type="number"
                inputMode="numeric"
                className="input"
                placeholder="0"
                value={showNum(override.lineExtra)}
                onChange={(e) => onChange('lineExtra', toNum(e.target.value))}
              />
            </Field>
          </div>

          <Field label="Line Profit %">
            <input
              type="number"
              inputMode="decimal"
              step="0.5"
              className="input"
              placeholder="0"
              value={showNum(override.profitPct)}
              onChange={(e) => onChange('profitPct', toNum(e.target.value))}
            />
          </Field>

          <Field label="Notes (optional)">
            <input
              className="input"
              placeholder="Shown under item on PDF"
              value={override.notes ?? ''}
              onChange={(e) => onChange('notes', e.target.value)}
            />
          </Field>
        </div>
      )}
    </div>
  )
}

// ─── printable quote (shared header + meta) ─────────────────────────────────

const PrintShell = ({ children }) => (
  <div
    style={{
      fontFamily: 'Inter, sans-serif',
      color: '#1a202c',
      padding: '8px',
      maxWidth: '800px',
      margin: '0 auto',
    }}
  >
    {children}
  </div>
)

const PrintHeader = ({
  quoteNo,
  label = 'QUOTATION',
  packageName,
  serviceType,
  internal = false,
}) => (
  <>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '3px solid #0f766e',
        paddingBottom: '12px',
        marginBottom: '12px',
        gap: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minWidth: 0,
          flex: '1 1 auto',
        }}
      >
        <img
          src="/assets/images/logo.png"
          alt=""
          style={{ height: '56px', flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '22px',
              fontWeight: 800,
              color: '#0f766e',
              letterSpacing: '-0.2px',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
            }}
          >
            AuraSquare Events
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
            24, Mettu Street, Kundrathur, Chennai 600069
          </div>
          <div style={{ fontSize: '11px', color: '#64748b' }}>
            +91 94448 48616 · aurasquare.events@gmail.com
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {internal && (
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '9px',
              fontWeight: 800,
              letterSpacing: '3px',
              color: '#dc2626',
              marginBottom: '2px',
              whiteSpace: 'nowrap',
            }}
          >
            INTERNAL COPY
          </div>
        )}
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '20px',
            fontWeight: 800,
            letterSpacing: '4px',
            color: '#134e4a',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: '11px',
            color: '#64748b',
            marginTop: '4px',
            whiteSpace: 'nowrap',
          }}
        >
          No. {quoteNo}
          <br />
          Issued: {new Date().toLocaleDateString('en-IN')}
        </div>
      </div>
    </div>

    {(packageName || serviceType) && (
      <div
        style={{
          background: '#0f766e',
          color: '#fff',
          textAlign: 'center',
          padding: '14px 14px 16px',
          borderRadius: '8px',
          marginBottom: '18px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1.2,
        }}
      >
        {serviceType && (
          <div
            style={{
              // When package name is shown below, keep the service type as a
              // smaller eyebrow line. When it's alone (e.g. comparison view),
              // promote it to the headline size.
              fontSize: packageName ? '11px' : '20px',
              letterSpacing: packageName ? '5px' : '6px',
              fontWeight: 800,
              opacity: packageName ? 0.85 : 1,
              marginBottom: packageName ? '4px' : 0,
              textTransform: 'uppercase',
            }}
          >
            {String(serviceType).toUpperCase()} SERVICES
          </div>
        )}
        {packageName && (
          <div
            style={{
              fontSize: '16px',
              fontWeight: 800,
              letterSpacing: '4px',
            }}
          >
            {String(packageName).toUpperCase()} PACKAGE
          </div>
        )}
      </div>
    )}
  </>
)

const PrintMeta = ({ event }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '6px 24px',
      fontSize: '12px',
      marginBottom: '18px',
      background: '#f8fafc',
      padding: '12px 16px',
      borderRadius: '8px',
    }}
  >
    <MetaRow label="Customer" value={fullName(event) || '—'} />
    <MetaRow label="Contact" value={fullContact(event) || '—'} />
    <MetaRow label="Event" value={event.eventType} />
    <MetaRow label="Guests" value={event.guestCount} />
    <MetaRow
      label="Date & Time"
      value={`${
        event.date
          ? new Date(event.date).toLocaleDateString('en-IN', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })
          : '—'
      }  ·  ${fmtTimeRange(event.startTime || event.time, event.endTime)}`}
    />
    <MetaRow label="Location" value={event.location || '—'} />
  </div>
)

// ─── client PDF: items + qty (conditional) + grand total only ───────────────

// 3-column side-by-side Starter / Basic / Premium comparison.
// Client-style (no per-item amounts) — designed to fit on a single image.
const ComparisonQuote = ({
  event,
  starter,
  basic,
  premium,
  quoteNo,
  recommendedPackage = null,
}) => {
  const SANS = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"

  // Editorial tier palette — each tier reads as a distinct hue.
  // Starter (warm champagne) · Basic (cool mint) · Premium (blush + wine)
  const palettes = {
    Starter: {
      cardBg: '#fff9eb', // warm champagne
      border: '#a16207', // bronze
      accent: '#a16207',
      ink: '#3f2807',
      muted: '#7c5212',
      dividerBg: '#f3e8c5',
      isDark: false,
    },
    Basic: {
      cardBg: '#ecfdf5', // pale mint
      border: '#0f766e', // forest
      accent: '#0f766e',
      ink: '#0a3b34',
      muted: '#246b5e',
      dividerBg: '#c7ebde',
      isDark: false,
    },
    Premium: {
      cardBg: '#fdf2f8', // pale blush
      border: '#9f1239', // wine / rose
      accent: '#9f1239',
      ink: '#4c0519',
      muted: '#881337',
      dividerBg: '#f9d3e2',
      isDark: false,
    },
  }

  const Col = ({ pkg, view }) => {
    const p = palettes[pkg] || palettes.Starter
    const isRecommended = recommendedPackage === pkg
    const itemBorder = p.isDark
      ? '0.5px solid rgba(212, 175, 106, 0.2)'
      : '0.5px dashed #e7e2d6'
    return (
      <div
        style={{
          flex: 1,
          minWidth: 0,
          borderRadius: '14px',
          overflow: 'hidden',
          background: p.cardBg,
          border: `1px solid ${p.border}`,
          boxShadow: p.isDark
            ? '0 10px 30px rgba(15, 23, 42, 0.25)'
            : '0 2px 10px rgba(15, 23, 42, 0.04)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Premium ribbon — only on the recommended column. */}
        {isRecommended && (
          <div
            style={{
              background:
                'linear-gradient(90deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              color: '#d4af6a',
              fontSize: '11px',
              letterSpacing: '6px',
              fontWeight: 700,
              padding: '9px 8px 8px',
              textAlign: 'center',
              textTransform: 'uppercase',
              borderBottom: '1.5px solid #d4af6a',
              boxShadow: 'inset 0 -1px 0 rgba(212, 175, 106, 0.25)',
            }}
          >
            ★ RECOMMENDED
          </div>
        )}

        {/* Header — typographic, no big background fill */}
        <div
          style={{
            padding: '22px 14px 18px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontFamily: SANS,
              fontSize: '9px',
              color: p.muted,
              letterSpacing: '4px',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            Package
          </div>
          <div
            style={{
              fontFamily: SANS,
              fontSize: '24px',
              fontWeight: 800,
              color: p.ink,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              marginTop: '4px',
              lineHeight: 1.1,
            }}
          >
            {pkg}
          </div>
          <div
            style={{
              width: '40px',
              height: '2px',
              background: p.accent,
              margin: '14px auto',
            }}
          />
          <div
            style={{
              fontFamily: SANS,
              fontSize: '28px',
              fontWeight: 800,
              color: p.ink,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.5px',
              lineHeight: 1,
            }}
          >
            ₹ {(Number(view.pkgGrandTotal) || 0).toLocaleString('en-IN')}
          </div>
          <div
            style={{
              fontFamily: SANS,
              fontSize: '9px',
              color: p.muted,
              marginTop: '10px',
              letterSpacing: '2.5px',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            {view.pkgLines.length} item
            {view.pkgLines.length !== 1 && 's'} included
          </div>
        </div>

        {/* Decorative divider */}
        <div
          style={{
            position: 'relative',
            height: '1px',
            background: p.isDark ? '#1e293b' : '#e7e2d6',
            margin: '0 18px 12px',
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: '-7px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: p.cardBg,
              padding: '0 8px',
              color: p.accent,
              fontSize: '10px',
              fontWeight: 700,
            }}
          >
            ✦
          </span>
        </div>

        {/* Items list */}
        <div style={{ padding: '6px 16px 18px', flex: 1 }}>
          {view.pkgLines.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                color: p.muted,
                fontStyle: 'italic',
                fontSize: '11px',
                padding: '20px 0',
              }}
            >
              No items
            </div>
          ) : (
            view.pkgLines.map((line, i) => {
              const { item, override, qty, unitLabel } = line
              const isInclusive = item.rule === 'inclusive'
              const showQty = override.showQty === true
              const baseName = item.name || 'Custom item'
              const displayName =
                showQty && !isInclusive
                  ? `${baseName} (${qty} ${unitLabel})`
                  : baseName
              return (
                <div
                  key={item.id}
                  style={{
                    padding: '6px 0 6px 22px',
                    borderBottom:
                      i === view.pkgLines.length - 1 ? 'none' : itemBorder,
                    fontSize: '11px',
                    lineHeight: 1.4,
                    position: 'relative',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 7,
                      color: p.accent,
                      fontFamily: SANS,
                      fontWeight: 700,
                      fontSize: '10px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div
                    style={{
                      fontFamily: SANS,
                      fontWeight: 600,
                      color: p.ink,
                    }}
                  >
                    {displayName}
                  </div>
                  {item.description && (
                    <div
                      style={{
                        fontFamily: SANS,
                        fontStyle: 'italic',
                        fontSize: '9.5px',
                        color: p.muted,
                        whiteSpace: 'pre-line',
                        marginTop: '1px',
                      }}
                    >
                      {item.description}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  }

  return (
    <PrintShell>
      <PrintHeader
        quoteNo={quoteNo}
        label="QUOTATION"
        packageName={null}
        serviceType={event.serviceType}
      />
      <PrintMeta event={event} />


      <div style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
        <Col pkg="Starter" view={starter} />
        <Col pkg="Basic" view={basic} />
        <Col pkg="Premium" view={premium} />
      </div>

      {/* Inclusion note — bottom of the comparison table */}
      <div
        style={{
          textAlign: 'left',
          marginTop: '14px',
          fontFamily: SANS,
          fontSize: '11px',
          color: '#0f766e',
          fontWeight: 600,
          letterSpacing: '0.3px',
        }}
      >
        * Travel charges are included
      </div>

      <div
        style={{
          textAlign: 'center',
          marginTop: '14px',
        }}
      >
        <div
          style={{
            width: '36px',
            height: '1px',
            background: '#c8a96a',
            margin: '0 auto 8px',
          }}
        />
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '11px',
            color: '#475569',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          Thank you for choosing AuraSquare Events
        </div>
      </div>
    </PrintShell>
  )
}

const ClientQuote = ({ event, lines, grandTotal, quoteNo }) => {
  // Auto-compact mode: switch to 2-column layout + tighter spacing if many items
  const useTwoCols = lines.length > 8
  const compact = lines.length > 14
  return (
    <PrintShell>
      <div
        className="client-print"
        style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid' }}
      >
        <PrintHeader
          quoteNo={quoteNo}
          label="QUOTATION"
          packageName={event.packageName}
          serviceType={event.serviceType}
        />
        <PrintMeta event={event} />

        {lines.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '24px',
              color: '#94a3b8',
              fontStyle: 'italic',
              fontSize: '12px',
            }}
          >
            No items added.
          </div>
        ) : useTwoCols ? (
          (() => {
            const half = Math.ceil(lines.length / 2)
            const renderItem = (line, indexInColumn, columnOffset) => {
              const { item, override, qty, unitLabel } = line
              const showQty = override.showQty === true
              const isInclusive = item.rule === 'inclusive'
              const baseName = item.name || 'Custom item'
              const displayName =
                showQty && !isInclusive
                  ? `${baseName} (${qty} ${unitLabel})`
                  : baseName
              const realIndex = columnOffset + indexInColumn
              return (
                <div
                  key={item.id}
                  style={{
                    marginBottom: compact ? '4px' : '7px',
                    paddingLeft: '20px',
                    paddingBottom: compact ? '3px' : '5px',
                    borderBottom: '0.5px solid #e2e8f0',
                    position: 'relative',
                    lineHeight: 1.3,
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      color: '#94a3b8',
                      fontWeight: 500,
                      fontSize: compact ? '9.5px' : '10.5px',
                      width: '18px',
                    }}
                  >
                    {realIndex + 1}.
                  </span>
                  <div style={{ fontWeight: 600 }}>{displayName}</div>
                  {item.description && (
                    <div
                      style={{
                        fontSize: compact ? '9px' : '10px',
                        color: '#475569',
                        fontStyle: 'italic',
                        whiteSpace: 'pre-line',
                        marginTop: '1px',
                      }}
                    >
                      {item.description}
                    </div>
                  )}
                </div>
              )
            }
            return (
              <div
                style={{
                  display: 'flex',
                  gap: '20px',
                  fontSize: compact ? '10.5px' : '11.5px',
                  marginBottom: '16px',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  {lines.slice(0, half).map((l, i) => renderItem(l, i, 0))}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {lines.slice(half).map((l, i) => renderItem(l, i, half))}
                </div>
              </div>
            )
          })()
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '12px',
              marginBottom: '16px',
            }}
          >
            <colgroup>
              <col style={{ width: '36px' }} />
              <col />
            </colgroup>
            <thead>
              <tr style={{ background: '#0f766e', color: '#fff' }}>
                <th style={thStyle}>#</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Item</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(({ item, override, qty, unitLabel }, i) => {
                const showQty = override.showQty === true
                const isInclusive = item.rule === 'inclusive'
                const baseName = item.name || 'Custom item'
                const displayName =
                  showQty && !isInclusive
                    ? `${baseName} (${qty} ${unitLabel})`
                    : baseName
                return (
                  <tr
                    key={item.id}
                    style={{
                      borderBottom: '1px solid #e2e8f0',
                      verticalAlign: 'top',
                    }}
                  >
                    <td style={tdStyle}>{i + 1}</td>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>
                      <div style={{ fontWeight: 600 }}>{displayName}</div>
                      {item.description && (
                        <div
                          style={{
                            fontSize: '10.5px',
                            color: '#475569',
                            fontStyle: 'italic',
                            marginTop: '1px',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {item.description}
                        </div>
                      )}
                      {override.notes && (
                        <div style={{ fontSize: '10px', color: '#64748b' }}>
                          {override.notes}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        <div
          style={{
            marginTop: useTwoCols ? '14px' : '24px',
            background: 'linear-gradient(135deg, #0f766e 0%, #134e4a 100%)',
            color: '#fff',
            borderRadius: '12px',
            padding: useTwoCols ? '16px 24px' : '22px 28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 6px 18px rgba(15,118,110,0.28)',
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
          }}
        >
          <div>
            <div
              style={{
                fontSize: '11px',
                letterSpacing: '5px',
                textTransform: 'uppercase',
                fontWeight: 700,
                opacity: 0.85,
              }}
            >
              Grand Total
            </div>
            <div
              style={{
                fontSize: '10px',
                opacity: 0.7,
                marginTop: '4px',
                letterSpacing: '0.5px',
              }}
            >
              {event.guestCount} Guests · All Inclusive
            </div>
          </div>
          <div
            style={{
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: useTwoCols ? '28px' : '32px',
              fontWeight: 600,
              letterSpacing: '-0.2px',
              fontVariantNumeric: 'tabular-nums',
              fontFeatureSettings: '"tnum" 1, "kern" 1',
            }}
          >
            ₹ {(Number(grandTotal) || 0).toLocaleString('en-IN')}
          </div>
        </div>
      </div>
    </PrintShell>
  )
}

// ─── internal PDF: full breakdown ───────────────────────────────────────────

const InternalQuote = ({
  event,
  lines,
  itemsSubtotal,
  travelCharge,
  overallProfitPct,
  overallProfit,
  roundOff = 0,
  totalProfit = 0,
  totalProfitPct = 0,
  grandTotal,
  quoteNo,
}) => {
  const compact = lines.length > 8
  const tdPad = compact ? '5px 6px' : '10px 8px'
  const fontSize = compact ? '10.5px' : '11.5px'
  const subFont = compact ? '9px' : '10px'
  return (
  <PrintShell>
    <PrintHeader
      quoteNo={quoteNo}
      label="QUOTATION"
      packageName={event.packageName}
      serviceType={event.serviceType}
      internal
    />
    <PrintMeta event={event} />

    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize,
        marginBottom: '8px',
      }}
    >
      <colgroup>
        <col style={{ width: '28px' }} />
        <col />
        <col style={{ width: '60px' }} />
        <col style={{ width: '52px' }} />
        <col style={{ width: '74px' }} />
        <col style={{ width: '90px' }} />
      </colgroup>
      <thead>
        <tr style={{ background: '#0f172a', color: '#fff' }}>
          <th style={thStyle}>#</th>
          <th style={{ ...thStyle, textAlign: 'left' }}>Item</th>
          <th style={thStyle}>Qty</th>
          <th style={thStyle}>Unit</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Rate (₹)</th>
          <th style={{ ...thStyle, textAlign: 'right' }}>Amount (₹)</th>
        </tr>
      </thead>
      <tbody>
        {lines.length === 0 ? (
          <tr>
            <td
              colSpan="6"
              style={{
                textAlign: 'center',
                padding: '20px',
                color: '#94a3b8',
                fontStyle: 'italic',
              }}
            >
              No items added.
            </td>
          </tr>
        ) : (
          lines.map((line, i) => {
            const {
              item,
              override,
              qty,
              unitPrice,
              unitLabel,
              total,
              surchargeNote,
              lineExtra,
              profitPct,
              lineProfit,
            } = line
            const isInclusive = item.rule === 'inclusive'
            const detailParts = []
            if (surchargeNote) detailParts.push(surchargeNote)
            if (lineExtra > 0) detailParts.push(`extra ₹${lineExtra}`)
            if (profitPct > 0)
              detailParts.push(`${profitPct}% profit (₹${lineProfit})`)
            return (
              <tr
                key={item.id}
                style={{ borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' }}
              >
                <td style={{ ...tdStyle, padding: tdPad }}>{i + 1}</td>
                <td style={{ ...tdStyle, textAlign: 'left', padding: tdPad }}>
                  <div style={{ fontWeight: 600 }}>
                    {item.name || 'Custom item'}
                  </div>
                  {item.description && (
                    <div
                      style={{
                        fontSize: subFont,
                        color: '#475569',
                        fontStyle: 'italic',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {item.description}
                    </div>
                  )}
                  {override.notes && (
                    <div style={{ fontSize: subFont, color: '#64748b' }}>
                      {override.notes}
                    </div>
                  )}
                  {detailParts.length > 0 && (
                    <div style={{ fontSize: subFont, color: '#92400e' }}>
                      {detailParts.join(' · ')}
                    </div>
                  )}
                </td>
                <td style={{ ...tdStyle, padding: tdPad }}>
                  {isInclusive ? '—' : qty}
                </td>
                <td style={{ ...tdStyle, padding: tdPad }}>
                  {isInclusive ? '—' : unitLabel}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', padding: tdPad }}>
                  {isInclusive ? '—' : Number(unitPrice).toLocaleString('en-IN')}
                </td>
                <td
                  style={{
                    ...tdStyle,
                    textAlign: 'right',
                    fontWeight: 600,
                    padding: tdPad,
                  }}
                >
                  {isInclusive
                    ? 'Inclusive'
                    : Number(total).toLocaleString('en-IN')}
                </td>
              </tr>
            )
          })
        )}
      </tbody>
    </table>

    {/* Totals — plain block so each line can flow into the leftover space at
        the end of page 1 instead of getting orphaned to a new page. */}
    <div style={{ marginTop: compact ? '4px' : '8px' }}>
        <TotalLine label="Items Subtotal" value={itemsSubtotal} />
        {travelCharge > 0 && (
          <TotalLine label="Travel Charge" value={travelCharge} />
        )}
        {overallProfitPct > 0 && (
          <TotalLine
            label={`Overall Profit (${overallProfitPct}%)`}
            value={overallProfit}
          />
        )}
        {roundOff !== 0 && (
          <TotalLine
            label={roundOff > 0 ? 'Round Off (+)' : 'Round Off (−)'}
            value={roundOff}
          />
        )}
        {totalProfit !== 0 && (
          <TotalLine
            label={`Total Profit (${totalProfitPct}%)`}
            value={totalProfit}
            highlight
          />
        )}
        <TotalLine label="Grand Total" value={grandTotal} grand />
    </div>
  </PrintShell>
  )
}

const TotalLine = ({ label, value, grand, highlight }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: grand ? '12px 8px' : '6px 8px',
      borderTop: grand ? '2px solid #0f766e' : 'none',
      background: highlight ? '#fef3c7' : 'transparent',
      borderRadius: highlight ? '4px' : '0',
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
      marginTop: highlight ? '4px' : '0',
      marginBottom: highlight ? '4px' : '0',
    }}
  >
    <span
      style={{
        fontSize: '12px',
        fontWeight: grand || highlight ? 700 : 600,
        letterSpacing: grand ? '2px' : '0',
        textTransform: grand ? 'uppercase' : 'none',
        color: grand ? '#134e4a' : highlight ? '#92400e' : '#475569',
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        fontWeight: highlight ? 700 : 600,
        fontSize: grand ? '22px' : '12px',
        letterSpacing: grand ? '-0.2px' : '0',
        color: grand ? '#0f766e' : highlight ? '#92400e' : '#1a202c',
        fontVariantNumeric: 'tabular-nums',
        fontFeatureSettings: '"tnum" 1, "kern" 1',
      }}
    >
      ₹ {(Number(value) || 0).toLocaleString('en-IN')}
    </span>
  </div>
)

const thStyle = { padding: '8px 6px', fontSize: '11px', textAlign: 'center', fontWeight: 700 }
const tdStyle = { padding: '6px', textAlign: 'center' }

const MetaRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: '8px' }}>
    <span style={{ color: '#64748b', minWidth: '90px' }}>{label}:</span>
    <span style={{ fontWeight: 600 }}>{value}</span>
  </div>
)

const GlobalStyles = () => (
  <style>{`
    .input {
      width: 100%;
      padding: 0.75rem 0.875rem;
      border: 1px solid #cbd5e1;
      border-radius: 0.625rem;
      font-size: 16px;
      background: #fff;
      outline: none;
      transition: border-color .15s, box-shadow .15s;
      -webkit-appearance: none;
      appearance: none;
    }
    .input:focus {
      border-color: #1abc9c;
      box-shadow: 0 0 0 3px rgba(26,188,156,.18);
    }
    .input:disabled { background: #f1f5f9; color: #64748b; }
    .input-error {
      border-color: #f43f5e !important;
      background: #fff1f2;
    }
    .input-error:focus {
      box-shadow: 0 0 0 3px rgba(244,63,94,.18);
    }
    select.input {
      padding-right: 2.25rem;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%2364748b' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.875rem center;
    }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { scrollbar-width: none; }

    /* Render the printable area off-screen so html2canvas can capture it */
    .print-area {
      position: absolute;
      left: -10000px;
      top: 0;
      width: 800px;
      background: #fff;
    }
    @media print {
      @page { size: A4; margin: 12mm; }
      html, body { background: #fff !important; }
      .no-print { display: none !important; }
      .print-area {
        display: block !important;
        position: static !important;
        left: 0 !important;
        width: auto !important;
      }

      /* Allow tables to split across pages so rows don't get pushed
         to a new page while there's still vertical space available */
      .print-area table { page-break-inside: auto !important; }
      .print-area tbody { page-break-inside: auto !important; }
      .print-area tr {
        page-break-inside: avoid !important;
        page-break-after: auto !important;
        break-inside: avoid !important;
      }
      .print-area thead { display: table-header-group; }
      /* Client PDF must stay on a single page */
      .print-area .client-print {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
    }
  `}</style>
)

export default Quote
